import { useState, useEffect, useRef, useCallback } from "react";
import { GSTReconPage, CompliancePage, NoticesPage, DocumentsPage } from "./CAPages.jsx";
import { CAIntelligencePage } from "./CAIntelligencePage.jsx";
import ClientOnboardingWizard from "./ClientOnboardingWizard.jsx";
import JaanchAppV15 from "./JaanchApp_v15.jsx";
import * as api from "./api.js";
import { useAuth, useSearch as useSearchHook, useChat, useApi, usePolling } from "./hooks.js";
import {
  Search, MessageSquare, Bot, Bell, Settings, BarChart3, Users, Link2, BookOpen,
  Megaphone, Home, Activity, ChevronDown, ChevronRight, Send, ThumbsUp, ThumbsDown,
  Bookmark, ExternalLink, TrendingUp, Zap, X, Plus, Pin, Filter, Calendar, Clock,
  FileText, Globe, Shield, Eye, Sparkles, Copy, MoreHorizontal, ChevronLeft, Menu,
  Moon, Sun, AlertTriangle, PanelLeftClose, PanelLeft, Pencil, Download, Share2,
  ArrowUp, Paperclip, Keyboard, ArrowRight, CreditCard, Receipt, Gauge, CheckCircle, CircleDot,
  RefreshCw, UserPlus, Mail, Check, Loader, Info, Wifi, WifiOff, LogIn, ChevronUp,
  Brain, Sparkle, MessageCircle
} from "lucide-react";

// ╔═══════════════════════════════════════════════════════════════╗
// ║  DESIGN TOKENS                                                ║
// ╚═══════════════════════════════════════════════════════════════╝

const L = {
  bg:"#F7F8FB",sf:"#FFFFFF",sf2:"#F0F2F8",sfH:"#EAEDFA",
  bd:"#DFE3ED",bd2:"#ECEEF5",bdF:"#4F5BD5",
  tx:"#111827",tx2:"#4B5563",txM:"#6B7280",txD:"#9CA3AF",
  p:"#4F5BD5",pH:"#3F4ABF",pS:"rgba(79,91,213,.08)",pSf:"rgba(79,91,213,.04)",
  g:"#059669",gS:"rgba(5,150,105,.08)",o:"#D97706",oS:"rgba(217,119,6,.08)",
  r:"#DC2626",rS:"rgba(220,38,38,.08)",u:"#7C3AED",uS:"rgba(124,58,237,.08)",
  sh:"0 1px 3px rgba(0,0,0,.06)",shL:"0 4px 20px rgba(0,0,0,.08)",
  nv:"#1E1F3B",nvT:"#B8BADE",nvA:"#FFFFFF",
};

const D = {
  bg:"#0B0D19",sf:"#131524",sf2:"#191C2E",sfH:"#1E2240",
  bd:"#242848",bd2:"#1D2038",bdF:"#6366F1",
  tx:"#F0F1F8",tx2:"#A0A4BD",txM:"#6E7396",txD:"#4C5072",
  p:"#6366F1",pH:"#818CF8",pS:"rgba(99,102,241,.12)",pSf:"rgba(99,102,241,.06)",
  g:"#34D399",gS:"rgba(52,211,153,.12)",o:"#FBBF24",oS:"rgba(251,191,36,.12)",
  r:"#F87171",rS:"rgba(248,113,113,.12)",u:"#A78BFA",uS:"rgba(167,139,250,.12)",
  sh:"0 1px 3px rgba(0,0,0,.3)",shL:"0 4px 20px rgba(0,0,0,.4)",
  nv:"#0D0E1D",nvT:"#6E7396",nvA:"#E0E2F0",
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  MOCK DATA                                                    ║
// ╚═══════════════════════════════════════════════════════════════╝

const SRC = {
  gdocs:{i:"📝",n:"Google Docs"},gsheets:{i:"📊",n:"Sheets"},gdrive:{i:"📄",n:"Drive"},
  slack:{i:"💬",n:"Slack"},confl:{i:"📘",n:"Confluence"},jira:{i:"🎯",n:"Jira"},
  teams:{i:"👥",n:"Teams"},sp:{i:"📂",n:"SharePoint"},zoho:{i:"🧩",n:"Zoho CRM"},
  tally:{i:"📒",n:"Tally ERP"},gh:{i:"⚙️",n:"GitHub"},notion:{i:"📓",n:"Notion"},
  email:{i:"✉️",n:"Email"},wa:{i:"📱",n:"WhatsApp"},fresh:{i:"🎫",n:"Freshworks"},
  darwin:{i:"👤",n:"Darwinbox"},s3:{i:"☁️",n:"AWS S3"},
};

const RESULTS = [
  {id:"1",title:"Q3 Revenue Targets & Growth Strategy",snip:"Q3 revenue target is ₹45 Crore — 15% growth over Q2. Key accounts: Infosys, Wipro, TCS enterprise. Pipeline conversion target 28% with mid-market focus.",src:"gdocs",who:"Priya Sharma",when:"2h ago",v:true,go:"go/q3-targets",col:"Revenue Planning"},
  {id:"2",title:"Engineering Sprint Planning — Week 38",snip:"Sprint goals: SAML SSO integration, embedding service v2 with adaptive batching, Zoho CRM connector. Velocity: 84 story points. Blocked: AWS SES approval.",src:"confl",who:"Arjun Mehta",when:"45m ago"},
  {id:"3",title:"Customer Onboarding Playbook v3.1",snip:"Step 1: Discovery call within 24h. Step 2: Assign CSM. Step 3: Configure SSO. Step 4: Connector setup — prioritize Google Workspace and Slack.",src:"gdrive",who:"Neha Reddy",when:"3d ago",col:"Playbooks"},
  {id:"4",title:"DPDP Compliance Checklist for Enterprise",snip:"Data Protection and Digital Privacy Act 2023 requirements. Covers data residency, consent management, data principal rights, breach notification timelines.",src:"gdocs",who:"Vikram Iyer",when:"1w ago",v:true,go:"go/dpdp"},
  {id:"5",title:"Competitor Analysis — Glean vs COSFY",snip:"Feature comparison across 14 dimensions. COSFY advantages: sovereign deployment, Indian data residency, DPDP built-in, Tally/Zoho/Darwinbox connectors.",src:"gsheets",who:"Ravi Kumar",when:"5d ago"},
  {id:"6",title:"AWS Mumbai Cost Optimization Report",snip:"Monthly: EC2 ₹4.2L, RDS ₹2.8L, S3 ₹0.9L. Recommendations: Reserved instances for prod, spot for embedding workers, S3 Intelligent-Tiering.",src:"confl",who:"Suresh Patel",when:"2d ago"},
];

const ANN = [
  {id:1,t:"COSFY v0.3 — 20 Connectors",b:"Shipped Slack Bot, Teams Bot, and India-specific connectors.",e:"🚀",pin:true},
  {id:2,t:"Town Hall Friday 4 PM IST",b:"Q3 roadmap, Series A update, team celebrations.",e:"🎯",pin:false},
  {id:3,t:"DPDP Certification Achieved",b:"COSFY now DPDP-compliant for sovereign deployment.",e:"🛡️",pin:true},
];

const TREND = [
  {t:"API Rate Limiting Design Doc",s:"confl",v:234,d:"+42%"},
  {t:"Series A Due Diligence Pack",s:"gdrive",v:189,d:"+28%"},
  {t:"Competitor Analysis — Glean vs COSFY",s:"gsheets",v:156,d:"+19%"},
  {t:"India Data Center Architecture",s:"confl",v:142,d:"+15%"},
];

const REC = [
  {t:"Sprint Planning — Week 38",s:"confl",w:"45m ago"},
  {t:"SAML SSO Integration Notes",s:"gdocs",w:"2h ago"},
  {t:"Embedding Service v2 PRD",s:"notion",w:"Yesterday"},
  {t:"Q3 OKRs — Engineering",s:"gsheets",w:"Yesterday"},
];

const CAL = [
  {time:"10:00 AM",t:"Daily Standup",n:6,plat:"Meet",c:"#4F5BD5"},
  {time:"11:30 AM",t:"DPDP Compliance Review",n:3,plat:"Zoom",c:"#059669"},
  {time:"2:00 PM",t:"Infosys Enterprise Demo",n:8,plat:"Teams",c:"#D97706"},
  {time:"4:00 PM",t:"Sprint Retrospective",n:5,plat:"Meet",c:"#7C3AED"},
];

const PPL = [
  {nm:"Priya Sharma",rl:"Head of Sales",dp:"Sales",lc:"Mumbai",av:"PS",sk:["Enterprise Sales","Revenue Ops"]},
  {nm:"Arjun Mehta",rl:"Lead Engineer",dp:"Engineering",lc:"Bangalore",av:"AM",sk:["FastAPI","PostgreSQL","Search"]},
  {nm:"Neha Reddy",rl:"Customer Success",dp:"CS",lc:"Hyderabad",av:"NR",sk:["Onboarding","CSM"]},
  {nm:"Vikram Iyer",rl:"Compliance Lead",dp:"Legal",lc:"Chennai",av:"VI",sk:["DPDP","Data Sovereignty"]},
  {nm:"Ravi Kumar",rl:"Product Manager",dp:"Product",lc:"Bangalore",av:"RK",sk:["Strategy","Competitive Intel"]},
  {nm:"Suresh Patel",rl:"DevOps Engineer",dp:"Engineering",lc:"Pune",av:"SP",sk:["AWS","Docker","K8s"]},
];

const COLLS = [
  {id:1,nm:"New Hire Onboarding",em:"🎒",n:12,by:"Neha Reddy",w:"2d ago",ds:"Everything for the first 30 days"},
  {id:2,nm:"Revenue Planning",em:"📈",n:8,by:"Priya Sharma",w:"1w ago",ds:"Q3 targets, pipeline, forecasts"},
  {id:3,nm:"Engineering Playbooks",em:"⚙️",n:15,by:"Arjun Mehta",w:"3d ago",ds:"Architecture, runbooks, incidents"},
  {id:4,nm:"DPDP Compliance",em:"🛡️",n:6,by:"Vikram Iyer",w:"5d ago",ds:"Checklists, policies, audit trails"},
  {id:5,nm:"Competitor Intelligence",em:"🔍",n:9,by:"Ravi Kumar",w:"1w ago",ds:"Comparisons, win/loss reports"},
];

const GOLINKS = [
  {nm:"go/q3-targets",ds:"Q3 Revenue Targets",vi:342,by:"Priya"},
  {nm:"go/sprint",ds:"Current Sprint Board",vi:891,by:"Arjun"},
  {nm:"go/onboarding",ds:"New Hire Collection",vi:256,by:"Neha"},
  {nm:"go/dpdp",ds:"DPDP Checklist",vi:178,by:"Vikram"},
  {nm:"go/standup",ds:"Daily Standup Notes",vi:1204,by:"Arjun"},
  {nm:"go/roadmap",ds:"Product Roadmap 2026",vi:445,by:"Ravi"},
  {nm:"go/expenses",ds:"Expense Form",vi:623,by:"HR"},
];

const CONN = [
  {nm:"Google Workspace",st:"healthy",dc:"45,230",sy:"2m ago",ic:"🔵"},
  {nm:"Slack",st:"healthy",dc:"31,102",sy:"Real-time",ic:"💬"},
  {nm:"Jira",st:"healthy",dc:"12,847",sy:"5m ago",ic:"🎯"},
  {nm:"Confluence",st:"degraded",dc:"18,943",sy:"45m ago",ic:"📘"},
  {nm:"Microsoft Teams",st:"healthy",dc:"8,201",sy:"3m ago",ic:"👥"},
  {nm:"Zoho CRM",st:"healthy",dc:"5,634",sy:"15m ago",ic:"🧩"},
  {nm:"Tally ERP",st:"healthy",dc:"2,546",sy:"1h ago",ic:"📒"},
  {nm:"GitHub",st:"healthy",dc:"9,412",sy:"10m ago",ic:"⚙️"},
  {nm:"Freshworks",st:"healthy",dc:"3,891",sy:"20m ago",ic:"🎫"},
  {nm:"Darwinbox",st:"healthy",dc:"1,205",sy:"30m ago",ic:"👤"},
  {nm:"WhatsApp Business",st:"healthy",dc:"4,567",sy:"5m ago",ic:"📱"},
  {nm:"AWS S3",st:"healthy",dc:"7,823",sy:"8m ago",ic:"☁️"},
];

const AGN = [
  {id:1,nm:"Meeting Prep",ic:"📋",ds:"Auto-generates briefing docs with attendee context and action items.",rn:1247,ct:"Productivity",st:"active"},
  {id:2,nm:"IT Helpdesk",ic:"🔧",ds:"Searches KB, resolves tickets, escalates with full context.",rn:3891,ct:"IT",st:"active"},
  {id:3,nm:"New Hire Onboarding",ic:"🎒",ds:"Personalized 30-day journey with role-specific resources.",rn:234,ct:"HR",st:"active"},
  {id:4,nm:"Compliance Review",ic:"🛡️",ds:"Checks docs against DPDP, flags non-compliant sections.",rn:567,ct:"Legal",st:"active"},
  {id:5,nm:"Sales Meeting Prep",ic:"💼",ds:"CRM intel, competitive data, pre-call brief generation.",rn:892,ct:"Sales",st:"active"},
  {id:6,nm:"Incident Response",ic:"🚨",ds:"Runbook search, incident correlation, post-mortem drafting.",rn:156,ct:"Engineering",st:"active"},
  {id:7,nm:"Weekly Digest",ic:"📰",ds:"Personalized summary of decisions and action items weekly.",rn:2103,ct:"Productivity",st:"active"},
  {id:8,nm:"RFP Answerer",ic:"📝",ds:"Extracts RFP questions, drafts answers from company knowledge.",rn:78,ct:"Sales",st:"draft"},
];

const NOTIF = [
  {ty:"doc",ti:"Q3 Revenue doc updated",bd:"Priya updated projections",tm:"5m ago",ur:true},
  {ty:"agt",ti:"Meeting Prep completed",bd:"Briefing ready for 2 PM Infosys",tm:"12m ago",ur:true},
  {ty:"con",ti:"Confluence sync delayed",bd:"Last sync 45 minutes ago",tm:"45m ago",ur:true},
  {ty:"men",ti:"Arjun mentioned you",bd:"\"@AG prioritize Tally connector?\"",tm:"1h ago",ur:false},
  {ty:"agt",ti:"Compliance Review done",bd:"3 docs flagged for DPDP",tm:"2h ago",ur:false},
  {ty:"doc",ti:"Onboarding Playbook v3.2",bd:"Neha added SSO section",tm:"3h ago",ur:false},
];

const ACTV = [
  {ty:"search",ti:'Searched: "DPDP compliance"',tm:"2m ago"},
  {ty:"view",ti:"Viewed: Q3 Revenue Targets",tm:"15m ago"},
  {ty:"agent",ti:"Agent: Meeting Prep done",tm:"1h ago"},
  {ty:"chat",ti:"Chat: Architecture v0.4",tm:"3h ago"},
  {ty:"golink",ti:"Created: go/q3-review",tm:"4h ago"},
  {ty:"coll",ti:"Added 2 to Eng Playbooks",tm:"Yesterday"},
];

// ╔═══════════════════════════════════════════════════════════════╗
// ║  RESPONSIVE HOOK                                              ║
// ╚═══════════════════════════════════════════════════════════════╝

function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    let raf;
    const h = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setW(window.innerWidth)); };
    window.addEventListener("resize", h);
    return () => { window.removeEventListener("resize", h); cancelAnimationFrame(raf); };
  }, []);
  return { isMobile: w < 768, isTablet: w >= 768 && w < 1024, isDesktop: w >= 1024, w };
}

// ╔═══════════════════════════════════════════════════════════════╗
// ║  PRIMITIVES                                                   ║
// ╚═══════════════════════════════════════════════════════════════╝

const B = ({ children, color, bg, t }) => (
  <span style={{ background: bg || t.pS, color: color || t.p, padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, letterSpacing: .3, whiteSpace: "nowrap", lineHeight: "18px", display: "inline-block" }}>{children}</span>
);

const SI = ({ s, t }) => {
  const d = SRC[s] || { i: "📄", n: s };
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: t.txM }}><span style={{ fontSize: 12 }}>{d.i}</span><span style={{ textTransform: "uppercase", letterSpacing: .5, fontWeight: 500 }}>{d.n}</span></span>;
};

const Av = ({ ini, sz = 32, t }) => (
  <div style={{ width: sz, height: sz, borderRadius: "50%", background: `linear-gradient(135deg, ${t.p}, ${t.u})`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: Math.round(sz * .38), fontWeight: 600, flexShrink: 0 }}>{ini}</div>
);

const Btn = ({ children, onClick, primary, t, style: sx }) => (
  <button onClick={onClick} style={{
    display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 16px",
    background: primary ? t.p : "transparent", color: primary ? "#fff" : t.txM,
    border: primary ? "none" : `1px solid ${t.bd}`, borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: 600, fontFamily: "inherit", transition: "all .15s", ...sx,
  }}
    onMouseEnter={e => { if (primary) e.currentTarget.style.background = t.pH; else e.currentTarget.style.borderColor = t.p; }}
    onMouseLeave={e => { if (primary) e.currentTarget.style.background = t.p; else e.currentTarget.style.borderColor = t.bd; }}>
    {children}
  </button>
);

// ╔═══════════════════════════════════════════════════════════════╗
// ║  SIDEBAR                                                      ║
// ╚═══════════════════════════════════════════════════════════════╝

const NavI = ({ icon: Ic, label, active, onClick, badge, mini, t }) => (
  <button onClick={onClick} title={mini ? label : undefined} style={{
    display: "flex", alignItems: "center", gap: mini ? 0 : 10, width: "100%",
    padding: mini ? "10px 0" : "8px 12px", justifyContent: mini ? "center" : "flex-start",
    background: active ? "rgba(255,255,255,.1)" : "transparent",
    color: active ? t.nvA : t.nvT, border: "none", borderRadius: 8, cursor: "pointer",
    fontSize: 13, fontWeight: active ? 600 : 400, transition: "all .15s", position: "relative", fontFamily: "inherit",
  }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,.06)"; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
    <Ic size={18} style={{ flexShrink: 0 }} />
    {!mini && <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>}
    {!mini && badge && <span style={{ background: "#EF4444", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>{badge}</span>}
    {mini && badge && <span style={{ position: "absolute", top: 6, right: mini ? 10 : 12, width: 6, height: 6, borderRadius: "50%", background: "#EF4444" }} />}
  </button>
);

const UNREAD_COUNT = String(NOTIF.filter(n => n.ur).length);

const SidebarInner = ({ page, go, mini, setMini, t }) => {
  const sec = (label, items) => (
    <>
      {!mini && <div style={{ padding: "0 4px", margin: "6px 0" }}><span style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,.25)" }}>{label}</span></div>}
      {items.map(([Ic, lab, pg, bdg]) => <NavI key={pg} icon={Ic} label={lab} active={page === pg} onClick={() => go(pg)} badge={bdg} mini={mini} t={t} />)}
    </>
  );

  return (
    <div style={{ width: mini ? 60 : 220, background: t.nv, display: "flex", flexDirection: "column", padding: mini ? "16px 8px" : "16px 12px", transition: "width .2s", flexShrink: 0, borderRight: "1px solid rgba(255,255,255,.06)", height: "100%", overflow: "hidden" }}>
      {/* Logo */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: mini ? "4px 0" : "4px", marginBottom: 20, justifyContent: mini ? "center" : "flex-start" }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Zap size={16} color="#fff" /></div>
        {!mini && <span style={{ color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: -.3 }}>Jaanch AI</span>}
        {!mini && <div style={{ flex: 1 }} />}
        <button onClick={() => setMini(!mini)} style={{ background: "none", border: "none", cursor: "pointer", color: t.nvT, padding: 2, display: "flex" }}>
          {mini ? <PanelLeft size={15} /> : <PanelLeftClose size={15} />}
        </button>
      </div>

      {/* Primary */}
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {[[Home, "Home", "home"], [Search, "Search", "search"], [MessageSquare, "Chat", "chat"], [Bot, "Agents", "agents"]].map(([Ic, l, pg]) => (
          <NavI key={pg} icon={Ic} label={l} active={page === pg} onClick={() => go(pg)} mini={mini} t={t} />
        ))}
      </div>

      <div style={{ margin: mini ? "12px 0" : "12px 4px", height: 1, background: "rgba(255,255,255,.08)" }} />
      {sec("Knowledge", [[Link2, "Go Links", "golinks"], [BookOpen, "Collections", "collections"], [Megaphone, "Announcements", "announces"], [Users, "People", "people"], [Calendar, "Calendar", "calendar"]])}
      <div style={{ margin: mini ? "12px 0" : "12px 4px", height: 1, background: "rgba(255,255,255,.08)" }} />
      {sec("CA Suite", [[Brain, "CA Intelligence", "ca_chat"], [RefreshCw, "GST Recon", "gst_recon"], [CheckCircle, "Compliance", "compliance"], [Receipt, "Notices", "notices"], [FileText, "Documents", "documents"]])}
      <div style={{ margin: mini ? "12px 0" : "12px 4px", height: 1, background: "rgba(255,255,255,.08)" }} />
      {sec("Activity", [[Bell, "Notifications", "notifs", UNREAD_COUNT], [Activity, "Activity", "activity"]])}

      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <NavI icon={Wifi} label="Connectors" active={page === "connectors"} onClick={() => go("connectors")} mini={mini} t={t} />
        <NavI icon={UserPlus} label="Team" active={page === "team"} onClick={() => go("team")} mini={mini} t={t} />
        <NavI icon={CreditCard} label="Billing" active={page === "billing" || page === "pricing"} onClick={() => go("billing")} mini={mini} t={t} />
        <NavI icon={BarChart3} label="Admin" active={page === "admin"} onClick={() => go("admin")} mini={mini} t={t} />
        <NavI icon={Settings} label="Settings" active={page === "settings"} onClick={() => go("settings")} mini={mini} t={t} />
      </div>
      <div style={{ margin: "8px 0", height: 1, background: "rgba(255,255,255,.08)" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: mini ? "4px 0" : "4px", justifyContent: mini ? "center" : "flex-start" }}>
        <Av ini="AG" sz={28} t={t} />
        {!mini && <div><div style={{ color: "#fff", fontSize: 12, fontWeight: 600 }}>AG</div><div style={{ color: t.nvT, fontSize: 10 }}>Founder</div></div>}
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  TOPBAR                                                       ║
// ╚═══════════════════════════════════════════════════════════════╝

const TopBar = ({ q, setQ, doSearch, mode, setMode, openMob, isMobile, t, setKbd, searchRef }) => (
  <div style={{ height: 56, borderBottom: `1px solid ${t.bd}`, display: "flex", alignItems: "center", padding: "0 16px", gap: 10, background: t.sf, flexShrink: 0 }}>
    {isMobile && <button onClick={openMob} style={{ background: "none", border: "none", cursor: "pointer", color: t.txM, padding: 4, display: "flex" }}><Menu size={20} /></button>}
    <form onSubmit={e => { e.preventDefault(); doSearch(); }} style={{ flex: 1, maxWidth: 540 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 10, padding: "0 12px", height: 38 }}>
        <Search size={15} color={t.txD} />
        <input ref={searchRef} value={q} onChange={e => setQ(e.target.value)} placeholder={isMobile ? "Search..." : "Search across all tools... (⌘K)"}
          style={{ flex: 1, background: "none", border: "none", color: t.tx, fontSize: 13, outline: "none", fontFamily: "inherit", minWidth: 0 }} />
        {q && <button type="button" onClick={() => setQ("")} style={{ background: "none", border: "none", cursor: "pointer", color: t.txD, display: "flex", padding: 2 }}><X size={14} /></button>}
        {!isMobile && <kbd style={{ padding: "2px 6px", borderRadius: 4, border: `1px solid ${t.bd}`, fontSize: 10, color: t.txD, fontFamily: "inherit" }}>⌘K</kbd>}
      </div>
    </form>
    <div style={{ flex: 1 }} />
    <div style={{ display: "flex", gap: 2 }}>
      {!isMobile && <button onClick={() => setKbd(v => !v)} title="Shortcuts" style={{ background: "none", border: "none", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.txM }}><Keyboard size={16} /></button>}
      <button onClick={() => setMode(mode === "light" ? "dark" : "light")} title="Theme" style={{ background: "none", border: "none", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.txM }}>
        {mode === "light" ? <Moon size={16} /> : <Sun size={16} />}
      </button>
      <button title="Notifications" style={{ position: "relative", background: "none", border: "none", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: t.txM }}>
        <Bell size={16} /><span style={{ position: "absolute", top: 6, right: 6, width: 7, height: 7, borderRadius: "50%", background: t.r, border: `2px solid ${t.sf}` }} />
      </button>
    </div>
  </div>
);

// ╔═══════════════════════════════════════════════════════════════╗
// ║  KEYBOARD SHORTCUTS MODAL                                     ║
// ╚═══════════════════════════════════════════════════════════════╝

const KbdModal = ({ show, close, t }) => {
  if (!show) return null;
  const sc = [["⌘ K", "Focus search"], ["⌘ /", "Shortcuts"], ["G H", "Home"], ["G S", "Search"], ["G C", "Chat"], ["G A", "Agents"], ["Esc", "Close / Clear"]];
  return (
    <div onClick={close} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.sf, borderRadius: 16, border: `1px solid ${t.bd}`, padding: 28, width: "100%", maxWidth: 400, boxShadow: t.shL }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: t.tx, margin: 0 }}>Keyboard Shortcuts</h3>
          <button onClick={close} style={{ background: "none", border: "none", cursor: "pointer", color: t.txM }}><X size={18} /></button>
        </div>
        {sc.map(([k, d], i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < sc.length - 1 ? `1px solid ${t.bd2}` : "none" }}>
            <span style={{ fontSize: 13, color: t.tx2 }}>{d}</span>
            <kbd style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${t.bd}`, background: t.sf2, fontSize: 12, color: t.txM, fontFamily: "'DM Mono',monospace" }}>{k}</kbd>
          </div>
        ))}
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  HOME PAGE                                                    ║
// ╚═══════════════════════════════════════════════════════════════╝

const HomePage = ({ go, setQ, doSearch, bp, t }) => {
  const [cm, setCm] = useState("search");
  const [hq, setHq] = useState("");
  const [caHealth, setCaHealth] = useState(null);
  const sub = e => { e.preventDefault(); if (!hq.trim()) return; setQ(hq); if (cm === "search") { go("search"); } else go("chat"); };
  const hr = new Date().getHours();
  const gr = hr < 12 ? "morning" : hr < 17 ? "afternoon" : "evening";
  const gridCols = bp.isMobile ? "1fr" : bp.isTablet ? "1fr 1fr" : "1fr 1fr 1fr";

  // Real-time CA health via SSE (falls back to one-shot fetch on error)
  useEffect(() => {
    let es;
    try {
      // SSE stream — updates every 60s
      const token = localStorage.getItem("cosfy_access") || "";
      es = new EventSource(`/api/v1/ca/compliance/stream?token=${token}`);
      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.event !== "error") {
            setCaHealth({
              alerts: {
                stats: {
                  high_risk_count:   data.risk_level === "high"   ? data.overdue_count : 0,
                  medium_risk_count: data.risk_level === "medium" ? data.overdue_count : 0,
                },
                high_risk: data.high_risk || [],
              },
            });
          }
        } catch {}
      };
      es.onerror = () => {
        // SSE failed — fall back to one-shot REST
        es && es.close();
        fetch("/api/v1/ca/compliance/health", {
          headers: { Authorization: `Bearer ${token}` }
        })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setCaHealth(d); })
          .catch(() => {});
      };
    } catch {
      // EventSource not available (e.g. test environment)
    }
    return () => { es && es.close(); };
  }, []);

  return (
    <div style={{ flex: 1, overflowY: "auto" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: bp.isMobile ? "0 16px" : "0 24px" }}>
        <div style={{ paddingTop: bp.isMobile ? 36 : 52, paddingBottom: 36, textAlign: "center" }}>
          <h1 style={{ fontSize: bp.isMobile ? 22 : 28, fontWeight: 700, color: t.tx, marginBottom: 6 }}>Good {gr}, AG</h1>
          <p style={{ color: t.txM, fontSize: 14, marginBottom: 24 }}>Search across Tally, GST Portal, IT Portal, TRACES and more</p>
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 14, boxShadow: t.shL, overflow: "hidden" }}>
            <div style={{ display: "flex", borderBottom: `1px solid ${t.bd}` }}>
              {[["search", "⌕ Search"], ["chat", "💬 Chat"]].map(([m, l]) => (
                <button key={m} onClick={() => setCm(m)} style={{ flex: 1, padding: "10px 16px", background: "none", border: "none", borderBottom: `2px solid ${cm === m ? t.p : "transparent"}`, color: cm === m ? t.p : t.txM, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{l}</button>
              ))}
            </div>
            <form onSubmit={sub} style={{ display: "flex", alignItems: "center", padding: "6px 6px 6px 16px", gap: 8 }}>
              <input value={hq} onChange={e => setHq(e.target.value)} placeholder={cm === "search" ? "Search clients, GST, notices, filings..." : "Ask Jaanch AI anything..."} style={{ flex: 1, background: "none", border: "none", color: t.tx, fontSize: bp.isMobile ? 14 : 15, outline: "none", padding: "10px 0", fontFamily: "inherit", minWidth: 0 }} />
              <button type="submit" style={{ background: t.p, border: "none", borderRadius: 10, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                {cm === "search" ? <Search size={17} color="#fff" /> : <Send size={17} color="#fff" />}
              </button>
            </form>
          </div>
        </div>

        {/* CA Health Strip */}
        {caHealth && (caHealth.alerts?.stats?.high_risk_count > 0 || caHealth.alerts?.stats?.medium_risk_count > 0) && (
          <div style={{ marginBottom: 20 }}>
            {/* Critical alert bar */}
            {caHealth.alerts?.stats?.high_risk_count > 0 && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderLeft: "4px solid #EF4444", borderRadius: 10, padding: bp.isMobile ? "10px 12px" : "11px 16px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>🔴</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <span style={{ fontWeight: 700, color: "#991B1B", fontSize: 13 }}>
                    {caHealth.alerts.stats.high_risk_count} HIGH RISK{caHealth.alerts.stats.high_risk_count > 1 ? " items" : " item"} — immediate action required
                  </span>
                  {caHealth.alerts?.high_risk?.slice(0, 2).map((item, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#B91C1C", marginTop: 2 }}>
                      {item.client} — {item.issue}
                    </div>
                  ))}
                </div>
                <button onClick={() => go("compliance")} style={{ background: "#B91C1C", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
                  View All →
                </button>
              </div>
            )}
            {/* Medium risk bar */}
            {caHealth.alerts?.stats?.medium_risk_count > 0 && (
              <div style={{ background: "#FFF7ED", border: "1px solid #FED7AA", borderLeft: "4px solid #F59E0B", borderRadius: 10, padding: bp.isMobile ? "9px 12px" : "10px 16px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>🟡</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600, color: "#92400E", fontSize: 12 }}>
                    {caHealth.alerts.stats.medium_risk_count} filings due within 7 days
                  </span>
                </div>
                <button onClick={() => go("compliance")} style={{ background: "none", border: "1px solid #F59E0B", color: "#92400E", borderRadius: 6, padding: "4px 12px", fontSize: 11, cursor: "pointer", fontWeight: 700, whiteSpace: "nowrap" }}>
                  Calendar →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Announcements */}
        <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, marginBottom: 28, WebkitOverflowScrolling: "touch" }}>
          {ANN.map(a => (
            <div key={a.id} style={{ minWidth: bp.isMobile ? 250 : 280, flex: "0 0 auto", padding: "14px 16px", background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontSize: 22, flexShrink: 0 }}>{a.e}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: t.tx, fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{a.t}</div>
                <div style={{ color: t.txM, fontSize: 12, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{a.b}</div>
              </div>
              {a.pin && <Pin size={12} color={t.p} style={{ flexShrink: 0, marginTop: 2 }} />}
            </div>
          ))}
        </div>

        {/* Widget Grid */}
        <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 16, marginBottom: 32 }}>
          {/* Recent */}
          <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: "16px 18px" }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: t.tx, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><Clock size={13} color={t.txD} /> Recent</h3>
            {REC.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: i ? `1px solid ${t.bd2}` : "none" }}>
                <span style={{ fontSize: 12 }}>{SRC[d.s]?.i}</span>
                <span style={{ flex: 1, fontSize: 12, color: t.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.t}</span>
                <span style={{ fontSize: 10, color: t.txD, flexShrink: 0 }}>{d.w}</span>
              </div>
            ))}
          </div>
          {/* Trending */}
          <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: "16px 18px" }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: t.tx, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><TrendingUp size={13} color={t.txD} /> Trending</h3>
            {TREND.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: i ? `1px solid ${t.bd2}` : "none" }}>
                <span style={{ fontSize: 12 }}>{SRC[d.s]?.i}</span>
                <span style={{ flex: 1, fontSize: 12, color: t.tx, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.t}</span>
                <span style={{ fontSize: 10, color: t.g, fontWeight: 500, flexShrink: 0 }}>{d.d}</span>
              </div>
            ))}
          </div>
          {/* Calendar */}
          <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: "16px 18px" }}>
            <h3 style={{ fontSize: 12, fontWeight: 600, color: t.tx, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 6 }}><Calendar size={13} color={t.txD} /> Today</h3>
            {CAL.map((ev, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0", borderTop: i ? `1px solid ${t.bd2}` : "none" }}>
                <div style={{ width: 3, height: 28, borderRadius: 2, background: ev.c, flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: t.tx, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.t}</div>
                  <div style={{ fontSize: 10, color: t.txD }}>{ev.time} · {ev.n}</div>
                </div>
                <button style={{ padding: "3px 8px", background: t.pS, color: t.p, border: "none", borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}>Join</button>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{ marginBottom: 40 }}>
          <h3 style={{ fontSize: 12, fontWeight: 600, color: t.txM, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}><Zap size={13} /> Quick Actions</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["New Go Link", Link2, "golinks"], ["New Collection", BookOpen, "collections"], ["CA Intelligence", Brain, "ca_chat"], ["GST Recon", RefreshCw, "gst_recon"], ["Compliance", CheckCircle, "compliance"], ["Notices", Receipt, "notices"]].map(([l, Ic, pg], i) => (
              <button key={i} onClick={() => go(pg)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 8, cursor: "pointer", color: t.tx2, fontSize: 12, fontWeight: 500, fontFamily: "inherit" }}>
                <Ic size={14} />{l}
              </button>
            ))}
            <button onClick={() => setShowClientWizard(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: t.p, border: "none", borderRadius: 8, cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "inherit" }}>
              <UserPlus size={14} /> Add Client
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  SEARCH PAGE                                                  ║
// ╚═══════════════════════════════════════════════════════════════╝

const SearchPage = ({ q, bp, t, user, backendLive }) => {
  const [flt, setFlt] = useState("all");
  const [liveResults, setLiveResults] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveAnswer, setLiveAnswer] = useState(null);

  // Try live API search
  useEffect(() => {
    if (!q?.trim() || !backendLive) return;
    let cancelled = false;
    setLiveLoading(true);
    api.search(q, { orgId: user?.org_id || "", userEmail: user?.email || "", sources: flt !== "all" ? [flt] : [] })
      .then(data => { if (!cancelled) { setLiveResults(data); setLiveAnswer(data.verified_answer || data.ai_answer || null); } })
      .catch(() => { if (!cancelled) setLiveResults(null); })
      .finally(() => { if (!cancelled) setLiveLoading(false); });
    return () => { cancelled = true; };
  }, [q, flt, backendLive]);

  // Fallback to mock data
  const textMatch = q ? RESULTS.filter(r => r.title.toLowerCase().includes(q.toLowerCase()) || r.snip.toLowerCase().includes(q.toLowerCase())) : [];
  const filtered = flt === "all" ? textMatch : textMatch.filter(r => r.src === flt);
  const hasRealResults = filtered.length > 0;

  // Use live data if available, otherwise mock
  const useLive = liveResults && liveResults.results?.length > 0;
  const displayResults = useLive
    ? liveResults.results.map(r => ({ id: r.document_id, title: r.title, snip: r.snippet, src: r.source_type, who: r.author_name, when: r.updated_at_human || "recently", v: r.verified, go: r.go_link, col: r.collection }))
    : (hasRealResults ? filtered : (q ? [] : RESULTS));
  const res = displayResults;
  const srcs = [...new Set(RESULTS.map(r => r.src))];

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ display: "flex", gap: 24, maxWidth: 1100, margin: "0 auto", flexDirection: bp.isMobile ? "column" : "row" }}>
        {/* Results */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <span style={{ color: t.txM, fontSize: 13 }}>{res.length} results</span>
            {q && <span style={{ color: t.txD, fontSize: 12 }}>· 38ms</span>}
          </div>

          {hasRealResults && (
            <div style={{ padding: bp.isMobile ? 16 : 20, background: `linear-gradient(135deg, ${t.gS}, ${t.pS})`, borderRadius: 12, border: `1px solid ${t.bd}`, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <Sparkles size={15} color={t.p} />
                <span style={{ color: t.p, fontSize: 11, fontWeight: 700, letterSpacing: .5, textTransform: "uppercase" }}>AI Answer</span>
                <div style={{ flex: 1 }} />
                <B color={t.g} bg={t.gS} t={t}>Verified</B>
              </div>
              <p style={{ color: t.tx, fontSize: 14, lineHeight: 1.7, margin: "0 0 12px" }}>
                The Q3 revenue target is <strong>₹45 Crore</strong> — 15% growth over Q2. Key accounts: Infosys, Wipro, TCS. Pipeline conversion target: 28%.
                <sup style={{ color: t.p, cursor: "pointer", fontWeight: 600 }}>[1]</sup>
                <sup style={{ color: t.p, cursor: "pointer", fontWeight: 600 }}>[2]</sup>
              </p>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ color: t.txD, fontSize: 11 }}>Helpful?</span>
                <button style={{ background: t.gS, border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: t.g, fontSize: 11, fontWeight: 500, fontFamily: "inherit" }}><ThumbsUp size={11} /> Yes</button>
                <button style={{ background: t.rS, border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: t.r, fontSize: 11, fontWeight: 500, fontFamily: "inherit" }}><ThumbsDown size={11} /> No</button>
                {!bp.isMobile && <>
                  <div style={{ flex: 1 }} />
                  <button style={{ background: "none", border: `1px solid ${t.bd}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, color: t.txM, fontSize: 11, fontFamily: "inherit" }}><Pencil size={11} /> Edit in Canvas</button>
                </>}
              </div>
            </div>
          )}

          {res.map(r => (
            <div key={r.id} style={{ padding: bp.isMobile ? "14px 16px" : "16px 20px", background: t.sf, borderRadius: 10, border: `1px solid ${t.bd}`, marginBottom: 8, transition: "border-color .15s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = t.bdF}
              onMouseLeave={e => e.currentTarget.style.borderColor = t.bd}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
                <SI s={r.src} t={t} />
                <span style={{ color: t.txD, fontSize: 11 }}>· {r.who} · {r.when}</span>
                <div style={{ flex: 1 }} />
                {r.v && <B color={t.g} bg={t.gS} t={t}>✓ Verified</B>}
                {r.go && <B t={t}>{r.go}</B>}
                {r.col && <B color={t.u} bg={t.uS} t={t}>{r.col}</B>}
              </div>
              <h3 style={{ color: t.p, fontSize: 15, fontWeight: 600, margin: "0 0 6px", lineHeight: 1.3 }}>{r.title}</h3>
              <p style={{ color: t.tx2, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{r.snip}</p>
              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                {[[Bookmark, "Save"], [ExternalLink, "Open"], [Copy, "Copy"]].map(([Ic, l]) => (
                  <button key={l} style={{ background: "none", border: "none", color: t.txD, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontFamily: "inherit", padding: 0 }}><Ic size={12} /> {l}</button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Facets — desktop/tablet only */}
        {!bp.isMobile && (
          <div style={{ width: bp.isTablet ? 180 : 220, flexShrink: 0 }}>
            <div style={{ position: "sticky", top: 24 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: t.txM, textTransform: "uppercase", letterSpacing: .5, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><Filter size={12} /> Filters</h4>
              <div style={{ background: t.sf, borderRadius: 10, border: `1px solid ${t.bd}`, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.tx2, marginBottom: 8 }}>Source</div>
                <button onClick={() => setFlt("all")} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 8px", borderRadius: 6, border: "none", background: flt === "all" ? t.pS : "transparent", color: flt === "all" ? t.p : t.txM, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}><Globe size={12} /> All</button>
                {srcs.map(s => (
                  <button key={s} onClick={() => setFlt(s)} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 8px", borderRadius: 6, border: "none", background: flt === s ? t.pS : "transparent", color: flt === s ? t.p : t.txM, cursor: "pointer", fontSize: 12, fontFamily: "inherit", textAlign: "left", marginTop: 2 }}>
                    <span style={{ fontSize: 11 }}>{SRC[s]?.i}</span> {SRC[s]?.n}
                  </button>
                ))}
              </div>
              <div style={{ background: t.sf, borderRadius: 10, border: `1px solid ${t.bd}`, padding: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.tx2, marginBottom: 8 }}>Updated</div>
                {["Today", "This week", "This month", "Any time"].map((f, i) => (
                  <div key={f} style={{ padding: "5px 8px", borderRadius: 6, color: i === 0 ? t.p : t.txM, fontSize: 12, background: i === 0 ? t.pS : "transparent", cursor: "pointer" }}>{f}</div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  CHAT PAGE + CANVAS                                           ║
// ╚═══════════════════════════════════════════════════════════════╝

const ChatPage = ({ bp, t, backendLive }) => {
  const chat = useChat();
  const msgId = useRef(0);
  const mkId = () => ++msgId.current;
  const [msgs, setMsgs] = useState([{ id: 0, role: "assistant", content: "Hi AG! I'm your Cosfy AI assistant. I can search across all 20 connected tools to find answers, generate reports, and help you work faster. What do you need?", sources: [] }]);
  const [inp, setInp] = useState("");
  const [typing, setTyping] = useState(false);
  const [canvas, setCanvas] = useState(null);
  const canvasRef = useRef(null);
  const endRef = useRef(null);

  // Init conversation when backend is live
  useEffect(() => { if (backendLive) chat.startConversation(); }, [backendLive]);

  // Sync canvas ref
  useEffect(() => {
    if (canvas && canvasRef.current && canvasRef.current.innerText.trim() === "") {
      canvasRef.current.innerText = canvas.content;
    }
  }, [canvas]);

  // Sync streaming messages from chat hook
  useEffect(() => {
    if (chat.messages.length > 0) setMsgs(chat.messages);
  }, [chat.messages]);

  const send = async () => {
    if (!inp.trim()) return;
    const q = inp; setInp("");

    if (backendLive && chat.activeConv) {
      await chat.sendStreaming(q);
      return;
    }

    // Demo mode — mock response
    const uid = mkId();
    setMsgs(p => [...p, { id: uid, role: "user", content: q, sources: [] }]);
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      const isWrite = /write|draft|create|compose/i.test(q);
      const aid = mkId();
      setMsgs(p => [...p, {
        id: aid,
        role: "assistant",
        content: isWrite
          ? "I've drafted that in the Canvas panel. You can edit directly, refine sections, and export when ready.\n\nThe draft draws from 3 source documents."
          : "Based on Q3 Revenue Targets and Sprint Planning:\n\n• Revenue target: ₹45 Crore (15% growth over Q2)\n• Engineering focus: SAML SSO + embedding service v2\n• Tally connector prioritized this sprint\n• DPDP compliance audit scheduled next week\n\nWant me to dig deeper into any area?",
        sources: [{ title: "Q3 Revenue Targets", s: "gdocs" }, { title: "Sprint Planning — Week 38", s: "confl" }, { title: "DPDP Compliance Checklist", s: "gdocs" }],
      }]);
      if (isWrite && !bp.isMobile) {
        setCanvas({
          title: "Draft Document",
          content: "# Q3 Revenue Strategy Update\n\nThe Q3 revenue target of ₹45 Crore represents 15% growth over Q2.\n\n## Key Accounts\n\n- Infosys — Expansion across 3 additional BUs\n- Wipro — Pilot to full enterprise conversion\n- TCS — New evaluation by CTO office\n\n## Pipeline Focus\n\nConversion target: 28%\n\n1. Mid-market enterprises (500-5000 employees)\n2. Companies with Zoho/Tally stack\n3. Organizations needing data sovereignty\n\n## Competitive Positioning\n\nCOSFY's sovereign-first architecture provides clear differentiation vs Glean.\n\n---\n*Sources: Q3 Revenue Targets, Sales Pipeline, Competitive Analysis*"
        });
      }
    }, 1600);
  };

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, typing]);

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", maxWidth: canvas ? 560 : 760, margin: canvas ? 0 : "0 auto", padding: bp.isMobile ? "0 16px" : "0 24px", minWidth: 0 }}>
        <div style={{ flex: 1, overflowY: "auto", paddingTop: 24, paddingBottom: 16 }}>
          {msgs.map((m) => (
            <div key={m.id} style={{ marginBottom: 24, display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
              {m.role === "assistant" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "flex", alignItems: "center", justifyContent: "center" }}><Zap size={12} color="#fff" /></div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: t.tx2 }}>Cosfy</span>
                </div>
              )}
              <div style={{
                maxWidth: bp.isMobile ? "92%" : "85%", padding: "14px 18px",
                borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: m.role === "user" ? t.p : t.sf,
                border: m.role === "user" ? "none" : `1px solid ${t.bd}`,
                color: m.role === "user" ? "#fff" : t.tx, fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap",
              }}>{m.content}</div>
              {m.sources?.length > 0 && (
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  {m.sources.map((s, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 8, fontSize: 11, color: t.txM }}>
                      <span>{SRC[s.s]?.i}</span><span style={{ fontWeight: 500 }}>[{j + 1}]</span><span>{s.title}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {typing && (
            <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "flex", alignItems: "center", justifyContent: "center" }}><Zap size={12} color="#fff" /></div>
              <div style={{ padding: "12px 18px", background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 16, display: "flex", gap: 4 }}>
                {[0, 1, 2].map(n => <div key={n} style={{ width: 7, height: 7, borderRadius: "50%", background: t.txD, animation: `pulse 1.2s ease-in-out ${n * .2}s infinite` }} />)}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div style={{ padding: "12px 0", borderTop: `1px solid ${t.bd}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 14, padding: "4px 4px 4px 12px" }}>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: t.txD, display: "flex", padding: 4 }}><Paperclip size={16} /></button>
            <input value={inp} onChange={e => setInp(e.target.value)} onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Ask anything..." style={{ flex: 1, background: "none", border: "none", color: t.tx, fontSize: 14, outline: "none", fontFamily: "inherit", padding: "8px 0", minWidth: 0 }} />
            <button onClick={send} style={{ background: t.p, border: "none", borderRadius: 10, width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
              <ArrowUp size={16} color="#fff" />
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 6 }}>
            <span style={{ fontSize: 10, color: t.txD }}>Claude Opus 4.6 · Smart routing · connected sources</span>
          </div>
        </div>
      </div>

      {/* Canvas */}
      {canvas && !bp.isMobile && (
        <div style={{ width: bp.isTablet ? 340 : 440, borderLeft: `1px solid ${t.bd}`, background: t.sf, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${t.bd}`, gap: 8 }}>
            <Pencil size={14} color={t.p} />
            <span style={{ fontSize: 13, fontWeight: 600, color: t.tx, flex: 1 }}>{canvas.title}</span>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: t.txM, display: "flex" }}><Download size={14} /></button>
            <button style={{ background: "none", border: "none", cursor: "pointer", color: t.txM, display: "flex" }}><Share2 size={14} /></button>
            <button onClick={() => setCanvas(null)} style={{ background: "none", border: "none", cursor: "pointer", color: t.txM, display: "flex" }}><X size={14} /></button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
            <div ref={canvasRef} contentEditable suppressContentEditableWarning style={{ color: t.tx, fontSize: 14, lineHeight: 1.8, outline: "none", whiteSpace: "pre-wrap", fontFamily: "inherit" }} />
          </div>
          <div style={{ padding: "10px 16px", borderTop: `1px solid ${t.bd}`, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, color: t.txD, flex: 1 }}>3 sources cited</span>
            <Btn primary t={t} style={{ padding: "5px 12px", fontSize: 11 }}>Export to Docs</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  AGENTS PAGE                                                  ║
// ╚═══════════════════════════════════════════════════════════════╝

const AgentsPage = ({ bp, t }) => {
  const [f, setF] = useState("all");
  const [sel, setSel] = useState(null);
  const cats = ["all", ...new Set(AGN.map(a => a.ct))];
  const filt = f === "all" ? AGN : AGN.filter(a => a.ct === f);

  if (sel) {
    const a = AGN.find(x => x.id === sel);
    if (!a) return null;
    const steps = [{ s: "Trigger", d: "User invokes or scheduled", ic: "⚡" }, { s: "Search", d: "Query 20 connected sources", ic: "🔍" }, { s: "Analyze", d: "LLM processes with instructions", ic: "🧠" }, { s: "Generate", d: "Create structured output", ic: "📄" }, { s: "Deliver", d: "Send via Slack, email, or COSFY", ic: "🚀" }];
    return (
      <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <button onClick={() => setSel(null)} style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: t.txM, cursor: "pointer", fontSize: 13, marginBottom: 20, fontFamily: "inherit" }}><ChevronLeft size={16} /> Back</button>
          <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", gap: 16, marginBottom: 24, flexDirection: bp.isMobile ? "column" : "row" }}>
            <span style={{ fontSize: 48 }}>{a.ic}</span>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>{a.nm}</h2>
              <p style={{ color: t.txM, fontSize: 13, margin: "4px 0 8px" }}>{a.ds}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <B t={t}>{a.ct}</B><B color={t.g} bg={t.gS} t={t}>{a.rn.toLocaleString()} runs</B>
                {a.st === "draft" && <B color={t.o} bg={t.oS} t={t}>Draft</B>}
              </div>
            </div>
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 14 }}>Workflow</h3>
          <div style={{ background: t.sf2, borderRadius: 12, border: `1px solid ${t.bd}`, padding: bp.isMobile ? 16 : 24, marginBottom: 24 }}>
            {steps.map((s, i) => (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: t.sf, borderRadius: 8, border: `1px solid ${t.bd}` }}>
                  <span style={{ fontSize: 20 }}>{s.ic}</span>
                  <div><div style={{ fontSize: 13, fontWeight: 600, color: t.tx }}>{s.s}</div><div style={{ fontSize: 12, color: t.txM }}>{s.d}</div></div>
                </div>
                {i < steps.length - 1 && <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}><ChevronDown size={16} color={t.txD} /></div>}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn primary t={t} style={{ flex: 1 }}>Run Agent</Btn>
            <Btn t={t}>Edit</Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
          <div><h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Agents</h2><p style={{ color: t.txM, fontSize: 13, margin: "4px 0 0" }}>AI-powered knowledge work automation</p></div>
          <Btn primary t={t}><Plus size={15} /> New Agent</Btn>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap", marginTop: 16 }}>
          {cats.map(c => (
            <button key={c} onClick={() => setF(c)} style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${f === c ? t.p : t.bd}`, background: f === c ? t.pS : "transparent", color: f === c ? t.p : t.txM, fontSize: 12, fontWeight: 500, cursor: "pointer", textTransform: "capitalize", fontFamily: "inherit" }}>{c}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {filt.map(a => (
            <div key={a.id} onClick={() => setSel(a.id)} style={{ padding: 20, background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, cursor: "pointer", transition: "all .15s", position: "relative" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = t.p; }} onMouseLeave={e => { e.currentTarget.style.borderColor = t.bd; }}>
              {a.st === "draft" && <div style={{ position: "absolute", top: 12, right: 12 }}><B color={t.o} bg={t.oS} t={t}>Draft</B></div>}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 28 }}>{a.ic}</span>
                <div><div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{a.nm}</div><div style={{ color: t.txD, fontSize: 11 }}>{a.rn.toLocaleString()} runs · {a.ct}</div></div>
              </div>
              <p style={{ color: t.txM, fontSize: 12, margin: 0, lineHeight: 1.5 }}>{a.ds}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  SECONDARY PAGES                                              ║
// ╚═══════════════════════════════════════════════════════════════╝

const PeoplePg = ({ bp, t }) => {
  const [s, setS] = useState("");
  const f = PPL.filter(p => (p.nm + p.rl + p.dp).toLowerCase().includes(s.toLowerCase()));
  return (<div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 800, margin: "0 auto" }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 4 }}>People</h2>
    <p style={{ color: t.txM, fontSize: 13, marginBottom: 16 }}>Find colleagues, expertise, and org structure</p>
    <div style={{ display: "flex", alignItems: "center", gap: 8, background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 10, padding: "0 14px", height: 40, marginBottom: 16 }}>
      <Search size={15} color={t.txD} /><input value={s} onChange={e => setS(e.target.value)} placeholder="Search..." style={{ flex: 1, background: "none", border: "none", color: t.tx, fontSize: 13, outline: "none", fontFamily: "inherit", minWidth: 0 }} />
    </div>
    {f.map((p, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: bp.isMobile ? 12 : 16, padding: bp.isMobile ? "12px 14px" : "14px 20px", background: t.sf, borderRadius: 10, border: `1px solid ${t.bd}`, marginBottom: 8 }}>
        <Av ini={p.av} sz={bp.isMobile ? 38 : 44} t={t} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{p.nm}</div>
          <div style={{ color: t.txM, fontSize: 12, marginTop: 2 }}>{p.rl} · {p.dp} · {p.lc}</div>
          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>{p.sk.map((e, j) => <B key={j} t={t}>{e}</B>)}</div>
        </div>
      </div>
    ))}
  </div></div>);
};

const CollsPg = ({ bp, t }) => (
  <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 800, margin: "0 auto" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
      <div><h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Collections</h2></div>
      <Btn primary t={t}><Plus size={15} /> New</Btn>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
      {COLLS.map(c => (
        <div key={c.id} style={{ padding: "18px 20px", background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 24 }}>{c.em}</span>
            <div style={{ flex: 1 }}><div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{c.nm}</div><div style={{ color: t.txD, fontSize: 11 }}>{c.n} items · {c.w}</div></div>
          </div>
          <p style={{ color: t.txM, fontSize: 12, margin: "0 0 10px", lineHeight: 1.4 }}>{c.ds}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Av ini={c.by.split(" ").map(n => n[0]).join("")} sz={20} t={t} /><span style={{ fontSize: 11, color: t.txD }}>{c.by}</span></div>
        </div>
      ))}
    </div>
  </div></div>
);

const GoLinksPg = ({ bp, t }) => (
  <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 800, margin: "0 auto" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Go Links</h2>
      <Btn primary t={t}><Plus size={15} /> New</Btn>
    </div>
    <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, overflow: "hidden" }}>
      {!bp.isMobile && (
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 70px 100px", padding: "10px 20px", borderBottom: `1px solid ${t.bd}`, gap: 12 }}>
          {["Go Link", "Destination", "Visits", "By"].map(h => <span key={h} style={{ fontSize: 11, fontWeight: 600, color: t.txD, textTransform: "uppercase", letterSpacing: .5 }}>{h}</span>)}
        </div>
      )}
      {GOLINKS.map((g, i) => bp.isMobile ? (
        <div key={i} style={{ padding: "12px 16px", borderBottom: i < GOLINKS.length - 1 ? `1px solid ${t.bd2}` : "none" }}>
          <div style={{ color: t.p, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", marginBottom: 4 }}>{g.nm}</div>
          <div style={{ color: t.tx2, fontSize: 12 }}>{g.ds}</div>
          <div style={{ color: t.txD, fontSize: 11, marginTop: 4 }}>{g.vi} visits · {g.by}</div>
        </div>
      ) : (
        <div key={i} style={{ display: "grid", gridTemplateColumns: "180px 1fr 70px 100px", padding: "12px 20px", borderBottom: i < GOLINKS.length - 1 ? `1px solid ${t.bd2}` : "none", gap: 12, alignItems: "center" }}>
          <span style={{ color: t.p, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{g.nm}</span>
          <span style={{ color: t.tx2, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.ds}</span>
          <span style={{ color: t.txM, fontSize: 12 }}>{g.vi.toLocaleString()}</span>
          <span style={{ color: t.txD, fontSize: 12 }}>{g.by}</span>
        </div>
      ))}
    </div>
  </div></div>
);

const AnnPg = ({ bp, t }) => (
  <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 700, margin: "0 auto" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Announcements</h2>
      <Btn primary t={t}><Plus size={15} /> New</Btn>
    </div>
    {ANN.map(a => (
      <div key={a.id} style={{ padding: "16px 18px", background: t.sf, borderRadius: 12, border: `1px solid ${a.pin ? t.p + "33" : t.bd}`, display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 26, flexShrink: 0 }}>{a.e}</span>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{a.t}</span>
            {a.pin && <B color={t.p} bg={t.pS} t={t}>Pinned</B>}
          </div>
          <p style={{ color: t.txM, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{a.b}</p>
        </div>
      </div>
    ))}
  </div></div>
);

const NotifPg = ({ t, bp }) => {
  const im = { doc: FileText, agt: Bot, con: AlertTriangle, men: MessageSquare };
  const cm = { doc: t.p, agt: t.g, con: t.o, men: t.u };
  return (<div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 700, margin: "0 auto" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Notifications</h2>
      <button style={{ padding: "6px 14px", background: t.pS, color: t.p, border: "none", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>Mark all read</button>
    </div>
    {NOTIF.map((n, i) => { const Ic = im[n.ty] || Bell; return (
      <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: bp.isMobile ? "12px 14px" : "14px 16px", background: n.ur ? t.pSf : "transparent", borderRadius: 10, marginBottom: 2 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: n.ur ? t.pS : t.sf2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={16} color={cm[n.ty] || t.txD} /></div>
        <div style={{ flex: 1, minWidth: 0 }}><div style={{ color: t.tx, fontSize: 13, fontWeight: n.ur ? 600 : 400 }}>{n.ti}</div><div style={{ color: t.txM, fontSize: 12, marginTop: 2 }}>{n.bd}</div></div>
        <span style={{ color: t.txD, fontSize: 11, flexShrink: 0 }}>{n.tm}</span>
        {n.ur && <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.p, flexShrink: 0, marginTop: 6 }} />}
      </div>
    ); })}
  </div></div>);
};

const ActPg = ({ t, bp }) => {
  const im = { search: Search, view: Eye, agent: Bot, chat: MessageSquare, golink: Link2, coll: BookOpen };
  return (<div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 700, margin: "0 auto" }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 20 }}>Activity</h2>
    {ACTV.map((a, i) => { const Ic = im[a.ty] || Activity; return (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: t.sf2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={14} color={t.txM} /></div>
        <span style={{ flex: 1, color: t.tx, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.ti}</span>
        <span style={{ color: t.txD, fontSize: 12, flexShrink: 0 }}>{a.tm}</span>
      </div>
    ); })}
  </div></div>);
};

const CalPg = ({ t, bp }) => (
  <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 700, margin: "0 auto" }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 4 }}>Calendar</h2>
    <p style={{ color: t.txM, fontSize: 13, marginBottom: 20 }}>{new Date().toLocaleDateString("en-IN", { weekday: "long", month: "long", day: "numeric" })}</p>
    {CAL.map((ev, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 16, padding: bp.isMobile ? "14px 16px" : "16px 20px", background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, marginBottom: 8 }}>
        <div style={{ width: 4, height: 44, borderRadius: 2, background: ev.c, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: t.tx }}>{ev.t}</div>
          <div style={{ fontSize: 12, color: t.txM, marginTop: 2 }}>{ev.time} · {ev.n} attendees · {ev.plat}</div>
        </div>
        <Btn primary t={t} style={{ padding: "6px 16px", fontSize: 12, flexShrink: 0 }}>Join</Btn>
      </div>
    ))}
  </div></div>
);

const AdminPg = ({ t, bp }) => {
  const stats = [{ l: "Active Users", v: "847", c: "+12%", cl: t.g }, { l: "Docs Indexed", v: "152,438", c: "+2,340", cl: t.p }, { l: "Searches", v: "34,291", c: "+18%", cl: t.u }, { l: "Latency", v: "38ms", c: "-8ms", cl: t.g }];
  const sc = { healthy: t.g, degraded: t.o, critical: t.r };
  return (<div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 960, margin: "0 auto" }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 20 }}>Admin</h2>
    <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 28 }}>
      {stats.map(s => (<div key={s.l} style={{ padding: bp.isMobile ? 14 : 18, background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}` }}>
        <div style={{ color: t.txD, fontSize: 11, textTransform: "uppercase", letterSpacing: .5, fontWeight: 600, marginBottom: 6 }}>{s.l}</div>
        <div style={{ color: t.tx, fontSize: bp.isMobile ? 20 : 26, fontWeight: 700 }}>{s.v}</div>
        <div style={{ color: s.cl, fontSize: 12, marginTop: 4, fontWeight: 500 }}>{s.c}</div>
      </div>))}
    </div>
    {/* Model Hub section */}
    <h3 style={{ fontSize: 16, fontWeight: 600, color: t.tx, marginBottom: 12 }}>🧠 LLM Model Hub</h3>
    <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, overflow: "hidden", marginBottom: 24 }}>
      {[
        { model: "Claude Opus 4.6",    provider: "Anthropic", tier: "complex",  pct: 15, cost: "$5/$25 per MTok",   badge: "Reasoning",  color: "#6D28D9" },
        { model: "Claude Sonnet 4.6",  provider: "Anthropic", tier: "standard", pct: 25, cost: "$3/$15 per MTok",   badge: "Balanced",   color: t.p },
        { model: "Claude Haiku 4.5",   provider: "Anthropic", tier: "simple",   pct: 60, cost: "$1/$5 per MTok",    badge: "Fast",       color: t.g },
        { model: "Groq (Llama 3.3)",   provider: "Groq",      tier: "fast",     pct: 0,  cost: "Fallback",          badge: "Fallback",   color: t.txD },
        { model: "Ollama (self-hosted)",provider: "Local",     tier: "local",    pct: 0,  cost: "Free",              badge: "Self-host",  color: t.txD },
      ].map((m, i, arr) => (
        <div key={m.model} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 20px",
          borderBottom: i < arr.length - 1 ? `1px solid ${t.bd2}` : "none", flexWrap: "wrap" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.pct > 0 ? m.color : t.bd, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 160 }}>
            <span style={{ color: t.tx, fontSize: 13, fontWeight: 600 }}>{m.model}</span>
            <span style={{ color: t.txD, fontSize: 11, marginLeft: 6 }}>{m.provider}</span>
          </div>
          {!bp.isMobile && <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: t.txM, width: 130 }}>{m.cost}</span>}
          {m.pct > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 80, height: 4, background: t.bd2, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${m.pct}%`, height: "100%", background: m.color, borderRadius: 2 }} />
              </div>
              <span style={{ fontSize: 11, color: t.txM, width: 30 }}>{m.pct}%</span>
            </div>
          )}
          <span style={{ fontSize: 10, background: m.color + "18", color: m.color,
            border: `1px solid ${m.color}30`, borderRadius: 4, padding: "2px 7px", fontWeight: 700 }}>{m.badge}</span>
        </div>
      ))}
      <div style={{ padding: "10px 20px", background: t.sf2, borderTop: `1px solid ${t.bd}`,
        fontSize: 11, color: t.txM, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <span>Routing: Haiku 60% · Sonnet 25% · Opus 15% · Est. savings vs pure Opus: ~62%</span>
        <span style={{ color: t.p, cursor: "pointer", fontWeight: 600 }}>Configure routing →</span>
      </div>
    </div>

    <h3 style={{ fontSize: 16, fontWeight: 600, color: t.tx, marginBottom: 12 }}>Connectors ({CONN.length})</h3>
    <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, overflow: "auto", marginBottom: 24 }}>
      {CONN.map((c, i) => (
        <div key={c.nm} style={{ display: "flex", alignItems: "center", padding: bp.isMobile ? "10px 14px" : "11px 20px", borderBottom: i < CONN.length - 1 ? `1px solid ${t.bd2}` : "none", gap: bp.isMobile ? 8 : 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: sc[c.st], flexShrink: 0 }} />
          <span style={{ fontSize: 14 }}>{c.ic}</span>
          <span style={{ color: t.tx, fontSize: 13, fontWeight: 500, minWidth: bp.isMobile ? 0 : 150, flex: bp.isMobile ? 1 : "none" }}>{c.nm}</span>
          {!bp.isMobile && <span style={{ color: t.txM, fontSize: 12, width: 90 }}>{c.dc} docs</span>}
          {!bp.isMobile && <span style={{ color: t.txD, fontSize: 12, flex: 1 }}>{c.sy}</span>}
          <B color={sc[c.st]} bg={c.st === "healthy" ? t.gS : c.st === "degraded" ? t.oS : t.rS} t={t}>{c.st === "healthy" ? "OK" : c.st.toUpperCase()}</B>
        </div>
      ))}
    </div>
    <div style={{ padding: "16px 20px", background: `linear-gradient(135deg, ${t.gS}, ${t.pS})`, borderRadius: 12, border: `1px solid ${t.bd}`, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      <Shield size={22} color={t.g} />
      <div style={{ flex: 1, minWidth: 180 }}><div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>DPDP Compliance — All Clear</div><div style={{ color: t.txM, fontSize: 12, marginTop: 2 }}>Indian data center active. Last audit: 3d ago.</div></div>
      <B color={t.g} bg={t.gS} t={t}>CERTIFIED</B>
    </div>
  </div></div>);
};

const SettingsPg = ({ mode: m, setMode: sm, t, bp, user, onLogout }) => (
  <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}><div style={{ maxWidth: 600, margin: "0 auto" }}>
    <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 24 }}>Settings</h2>
    {[["Appearance", (
      <div style={{ display: "flex", gap: 10 }}>
        {[["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([v, Ic, l]) => (
          <button key={v} onClick={() => sm(v)} style={{ flex: 1, padding: "14px 16px", borderRadius: 10, border: `2px solid ${m === v ? t.p : t.bd}`, background: m === v ? t.pS : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: m === v ? t.p : t.txM, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}><Ic size={16} />{l}</button>
        ))}
      </div>
    )], ["LLM Model Hub", (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {[["Claude Opus 4.6", "Complex reasoning", "15% of queries", true], ["Claude Sonnet 4.6", "Standard RAG", "25% of queries", true], ["Claude Haiku 4.5", "Simple factual", "60% of queries", true]].map(([p, md, r, a], i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 8, border: `1px solid ${t.bd}`, flexWrap: "wrap" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: a ? t.g : t.txD }} />
            <div style={{ flex: 1, minWidth: 100 }}><span style={{ color: t.tx, fontSize: 13, fontWeight: 600 }}>{p}</span><span style={{ color: t.txD, fontSize: 12 }}> · {md}</span></div>
            <B t={t}>{r}</B>
          </div>
        ))}
      </div>
    )], ["Data Sovereignty", (
      <div>
        <p style={{ color: t.txM, fontSize: 13, lineHeight: 1.6, margin: "0 0 12px" }}>All data processed and stored in Indian data centers. DPDP-compliant sovereign architecture.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><B color={t.g} bg={t.gS} t={t}>🇮🇳 India</B><B color={t.g} bg={t.gS} t={t}>DPDP</B><B t={t}>Sovereign</B></div>
      </div>
    )]].map(([title, content], i) => (
      <div key={i} style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: bp.isMobile ? 18 : 24, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 16 }}>{title}</h3>
        {content}
      </div>
    ))}
    <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: bp.isMobile ? 18 : 24, marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 16 }}>Account</h3>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Av ini={user?.name?.split(" ").map(n => n[0]).join("") || "AG"} sz={40} t={t} />
        <div><div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{user?.name || "AG"}</div><div style={{ color: t.txM, fontSize: 12 }}>{user?.email || "ag@aarama.in"} · {user?.role || "admin"}</div></div>
      </div>
      {onLogout && <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: t.rS, color: t.r, border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>Sign Out</button>}
    </div>
  </div></div>
);

// ╔═══════════════════════════════════════════════════════════════╗
// ║  PRICING PAGE                                                  ║
// ╚═══════════════════════════════════════════════════════════════╝

const PLAN_TIERS = [
  { slug: "starter", name: "Starter", price: 499, annual: 399, desc: "For small teams getting started with AI search", users: "10–50", connectors: 5, searches: "Unlimited", chats: "75 AI answers/mo", agents: "2 pre-built", features: ["Unlimited search", "5 connectors", "Email support (48h)", "AWS Mumbai data residency"], color: "#059669", popular: false, overage: "₹1.50/extra AI query" },
  { slug: "professional", name: "Pro", price: 899, annual: 699, desc: "For growing companies that need more AI power", users: "25–200", connectors: 15, searches: "Unlimited", chats: "200 AI answers/mo", agents: "5 + custom builder", features: ["Everything in Starter", "SSO / SAML", "API access", "Advanced analytics", "WhatsApp support (12h)", "Data export & audit log"], color: "#4F5BD5", popular: true, overage: "₹1.00/extra AI query" },
  { slug: "enterprise", name: "Business", price: 1249, annual: 999, desc: "For organizations that run on AI every day", users: "50–500", connectors: "Unlimited", searches: "Unlimited", chats: "500 AI answers/mo", agents: "Unlimited custom", features: ["Everything in Pro", "Unlimited connectors", "Custom agents", "Dedicated instance option", "Priority + dedicated CSM", "DPDP compliance pack"], color: "#7C3AED", popular: false, overage: "₹0.75/extra AI query" },
];

const PricingPage = ({ go, bp, t, backendLive }) => {
  const [annual, setAnnual] = useState(true);
  const [loading, setLoading] = useState(null);

  const handleSubscribe = async (tier) => {
    const slug = `${tier.slug}_${annual ? "annual" : "monthly"}`;
    if (!backendLive) { go("billing"); return; }
    setLoading(tier.slug);
    try {
      const res = await api.createSubscription(slug, 1);
      if (res.short_url) {
        window.location.href = res.short_url;
      } else if (res.razorpay_subscription_id) {
        const settings = await api.healthCheck();
        api.openRazorpayCheckout(
          res.razorpay_subscription_id,
          settings.razorpay_key_id || "",
          () => { go("billing"); },
          () => { setLoading(null); },
        );
      }
    } catch { setLoading(null); }
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h1 style={{ fontSize: bp.isMobile ? 24 : 32, fontWeight: 700, color: t.tx, marginBottom: 8 }}>Simple, transparent pricing</h1>
          <p style={{ color: t.txM, fontSize: 15, marginBottom: 24, maxWidth: 480, margin: "0 auto 24px" }}>Start free for 14 days. No credit card required. All prices in INR per user.</p>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 10, padding: 4 }}>
            <button onClick={() => setAnnual(false)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: !annual ? t.p : "transparent", color: !annual ? "#fff" : t.txM, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Monthly</button>
            <button onClick={() => setAnnual(true)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: annual ? t.p : "transparent", color: annual ? "#fff" : t.txM, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Annual <span style={{ fontSize: 10, opacity: .8 }}>Save 20%</span></button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16, marginBottom: 48 }}>
          {PLAN_TIERS.map(tier => (
            <div key={tier.slug} style={{ background: t.sf, borderRadius: 16, border: `2px solid ${tier.popular ? t.p : t.bd}`, padding: 28, position: "relative", display: "flex", flexDirection: "column" }}>
              {tier.popular && <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: t.p, color: "#fff", padding: "4px 14px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: .5 }}>MOST POPULAR</div>}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: tier.color }} />
                <h3 style={{ fontSize: 18, fontWeight: 700, color: t.tx, margin: 0 }}>{tier.name}</h3>
              </div>
              <p style={{ color: t.txM, fontSize: 13, marginBottom: 20, minHeight: 36 }}>{tier.desc}</p>
              <div style={{ marginBottom: 20 }}>
                <span style={{ fontSize: 36, fontWeight: 700, color: t.tx }}>₹{annual ? tier.annual : tier.price}</span>
                <span style={{ color: t.txM, fontSize: 13 }}>/user/mo</span>
                {annual && <div style={{ color: t.g, fontSize: 12, fontWeight: 500, marginTop: 4 }}>Billed annually · 2 months free</div>}
                {!annual && <div style={{ color: t.txD, fontSize: 11, marginTop: 4 }}>₹{tier.annual}/mo billed annually (save 20%)</div>}
              </div>
              <button onClick={() => handleSubscribe(tier)} disabled={loading === tier.slug}
                style={{ width: "100%", padding: "12px 0", borderRadius: 10, border: tier.popular ? "none" : `1px solid ${t.bd}`, background: tier.popular ? t.p : "transparent", color: tier.popular ? "#fff" : t.tx, fontSize: 14, fontWeight: 600, cursor: loading === tier.slug ? "wait" : "pointer", fontFamily: "inherit", marginBottom: 20, opacity: loading === tier.slug ? .7 : 1 }}>
                {loading === tier.slug ? "Processing..." : "Start 14-day free trial"}
              </button>
              <div style={{ flex: 1, borderTop: `1px solid ${t.bd2}`, paddingTop: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: t.txM, marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>Includes</div>
                {[
                  ["Team size", tier.users + " users"], ["Connectors", typeof tier.connectors === "number" ? tier.connectors : tier.connectors],
                  ["Search", tier.searches], ["AI Answers (Opus 4.6)", tier.chats], ["Agents", tier.agents],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", fontSize: 13 }}>
                    <span style={{ color: t.txM }}>{label}</span>
                    <span style={{ color: t.tx, fontWeight: 500 }}>{val}</span>
                  </div>
                ))}
                {tier.overage && <div style={{ padding: "6px 0 0", fontSize: 11, color: t.txD }}>Overage: {tier.overage}</div>}
                <div style={{ marginTop: 12 }}>
                  {tier.features.map((f, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0" }}>
                      <CheckCircle size={13} color={t.g} />
                      <span style={{ fontSize: 12, color: t.tx2 }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", padding: "24px 0 40px", borderTop: `1px solid ${t.bd2}` }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: t.tx, marginBottom: 8 }}>Enterprise Plus</h3>
          <p style={{ color: t.txM, fontSize: 14, marginBottom: 16, maxWidth: 420, margin: "0 auto 16px" }}>500+ users, on-premise deployment, custom SLA, or volume pricing? Let's build your package.</p>
          <button style={{ padding: "10px 28px", borderRadius: 10, border: `1px solid ${t.bd}`, background: "transparent", color: t.tx, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Contact Sales</button>
        </div>

        <div style={{ background: t.sf, borderRadius: 14, border: `1px solid ${t.bd}`, padding: bp.isMobile ? 20 : 32, marginBottom: 40 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: t.tx, marginBottom: 16 }}>Feature comparison</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${t.bd}` }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", color: t.txM, fontWeight: 600 }}>Feature</th>
                  {PLAN_TIERS.map(p => <th key={p.slug} style={{ textAlign: "center", padding: "10px 12px", color: t.tx, fontWeight: 600 }}>{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Unlimited semantic + keyword search", true, true, true],
                  ["AI-powered answers (Claude Opus 4.6)", "75/mo", "200/mo", "500/mo"],
                  ["SSO / SAML", false, true, true],
                  ["API Access", false, true, true],
                  ["Custom Agent Builder", false, false, true],
                  ["Advanced Analytics", false, true, true],
                  ["Data Export & Audit Log", false, true, true],
                  ["WhatsApp Support", false, true, true],
                  ["Dedicated Instance", false, false, true],
                  ["DPDP Compliance Pack", true, true, true],
                  ["Indian Data Residency (Mumbai)", true, true, true],
                ].map(([feat, ...vals], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.bd2}` }}>
                    <td style={{ padding: "10px 12px", color: t.tx2 }}>{feat}</td>
                    {vals.map((v, j) => (
                      <td key={j} style={{ textAlign: "center", padding: "10px 12px" }}>
                        {typeof v === "string" ? <span style={{ color: t.p, fontWeight: 600, fontSize: 12 }}>{v}</span> : v === true ? <CheckCircle size={15} color={t.g} /> : <span style={{ color: t.txD }}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  BILLING DASHBOARD                                            ║
// ╚═══════════════════════════════════════════════════════════════╝

const MOCK_USAGE = {
  search_queries: { current: 12840, limit: 99999, percentage: 12.8 },
  chat_messages: { current: 142, limit: 200, percentage: 71.0 },
  agent_runs: { current: 18, limit: 50, percentage: 36.0 },
  connectors: { current: 12, limit: 15, percentage: 80.0 },
  users: { current: 87, limit: 200, percentage: 43.5 },
};

const MOCK_INVOICES = [
  { invoice_number: "COSFY/2603/0004", total: 71818, status: "paid", paid_at: "2026-03-01", subtotal: 60863, igst: 10955 },
  { invoice_number: "COSFY/2602/0003", total: 71818, status: "paid", paid_at: "2026-02-01", subtotal: 60863, igst: 10955 },
  { invoice_number: "COSFY/2601/0002", total: 65482, status: "paid", paid_at: "2026-01-01", subtotal: 55494, igst: 9988 },
  { invoice_number: "COSFY/2512/0001", total: 58374, status: "paid", paid_at: "2025-12-01", subtotal: 49469, igst: 8905 },
];

const UsageMeter = ({ label, icon: Ic, current, limit, percentage, t }) => {
  const barColor = percentage > 90 ? t.r : percentage > 70 ? t.o : t.g;
  return (
    <div style={{ padding: "14px 18px", background: t.sf, borderRadius: 10, border: `1px solid ${t.bd}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Ic size={14} color={t.txD} />
          <span style={{ fontSize: 12, fontWeight: 600, color: t.tx2 }}>{label}</span>
        </div>
        <span style={{ fontSize: 12, color: t.txM }}>{current.toLocaleString()} / {limit.toLocaleString()}</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: t.sf2, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(percentage, 100)}%`, borderRadius: 3, background: barColor, transition: "width .5s ease" }} />
      </div>
      {percentage > 80 && <div style={{ fontSize: 11, color: t.o, marginTop: 6, fontWeight: 500 }}>{percentage >= 100 ? "Limit reached — upgrade to continue" : `${Math.round(100 - percentage)}% remaining`}</div>}
    </div>
  );
};

const BillingPage = ({ go, bp, t, backendLive, user }) => {
  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(MOCK_USAGE);
  const [invoices, setInvoices] = useState(MOCK_INVOICES);
  const [loadingSub, setLoadingSub] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!backendLive) { setLoadingSub(false); return; }
    (async () => {
      try {
        const data = await api.getSubscription();
        if (data.subscription) { setSub(data.subscription); setIsLive(true); }
        if (data.usage && Object.keys(data.usage).length) setUsage(data.usage);
        const inv = await api.listInvoices();
        if (inv.invoices?.length) setInvoices(inv.invoices);
      } catch {}
      setLoadingSub(false);
    })();
  }, [backendLive]);

  const currentPlan = sub?.plan || { name: "Pro", slug: "professional_annual", price_monthly: 699, limits: { max_users: 200, max_connectors: 15, max_searches: 99999, max_chats: 200, max_agent_runs: 50 } };

  const handleCancel = async () => {
    if (!confirm("Cancel your subscription? You'll retain access until the end of the billing period.")) return;
    setCancelling(true);
    try { await api.cancelSubscription(true); setSub(s => ({ ...s, cancel_at_period_end: true })); } catch {}
    setCancelling(false);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: 0 }}>Billing & Usage</h2>
            <p style={{ color: t.txM, fontSize: 13, margin: "4px 0 0" }}>Manage your subscription, monitor usage, and view invoices</p>
          </div>
          <Btn primary t={t} onClick={() => go("pricing")}><Sparkles size={15} /> View Plans</Btn>
        </div>

        {/* Current Plan Card */}
        <div style={{ background: `linear-gradient(135deg, ${t.pS}, ${t.sf})`, borderRadius: 14, border: `1px solid ${t.p}22`, padding: bp.isMobile ? 20 : 28, marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: bp.isMobile ? "flex-start" : "center", justifyContent: "space-between", flexDirection: bp.isMobile ? "column" : "row", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: t.txM, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5 }}>Current Plan</span>
                <B color={t.g} bg={t.gS} t={t}>{sub?.status === "active" ? "Active" : "Demo"}</B>
                {sub?.cancel_at_period_end && <B color={t.o} bg={t.oS} t={t}>Cancels at period end</B>}
              </div>
              <h3 style={{ fontSize: 24, fontWeight: 700, color: t.tx, margin: "0 0 4px" }}>{currentPlan.name}</h3>
              <p style={{ color: t.txM, fontSize: 14, margin: 0 }}>
                ₹{currentPlan.price_monthly || 699}/user/month · {sub?.quantity || 87} users · {sub?.billing_cycle || "annual"} billing
              </p>
              {sub?.current_period_end && <p style={{ color: t.txD, fontSize: 12, margin: "6px 0 0" }}>Next billing: {new Date(sub.current_period_end).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn primary t={t} onClick={() => go("pricing")}>Upgrade</Btn>
              {sub?.status === "active" && !sub?.cancel_at_period_end && (
                <Btn t={t} onClick={handleCancel} style={{ color: t.r, borderColor: t.r + "44" }}>
                  {cancelling ? "Cancelling..." : "Cancel"}
                </Btn>
              )}
            </div>
          </div>
        </div>

        {/* Usage Meters */}
        <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><Gauge size={15} color={t.txD} /> Usage This Period</h3>
        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "1fr 1fr", gap: 10, marginBottom: 28 }}>
          <UsageMeter label="Searches" icon={Search} current={usage.search_queries?.current || 0} limit={usage.search_queries?.limit || 25000} percentage={usage.search_queries?.percentage || 0} t={t} />
          <UsageMeter label="AI Answers (Opus 4.6)" icon={Sparkles} current={usage.chat_messages?.current || 0} limit={usage.chat_messages?.limit || 200} percentage={usage.chat_messages?.percentage || 0} t={t} />
          <UsageMeter label="Agent Runs" icon={Bot} current={usage.agent_runs?.current || 0} limit={usage.agent_runs?.limit || 100} percentage={usage.agent_runs?.percentage || 0} t={t} />
          <UsageMeter label="Connectors" icon={Link2} current={usage.connectors?.current || 0} limit={usage.connectors?.limit || 15} percentage={usage.connectors?.percentage || 0} t={t} />
          <UsageMeter label="Team Members" icon={Users} current={usage.users?.current || 0} limit={usage.users?.limit || 250} percentage={usage.users?.percentage || 0} t={t} />
        </div>

        {/* Invoice History */}
        <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><Receipt size={15} color={t.txD} /> Invoices</h3>
        <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, overflow: "hidden", marginBottom: 28 }}>
          {!bp.isMobile && (
            <div style={{ display: "grid", gridTemplateColumns: "180px 1fr 100px 100px 80px", padding: "10px 20px", borderBottom: `1px solid ${t.bd}`, gap: 12 }}>
              {["Invoice", "Date", "Subtotal", "GST (18%)", "Total"].map(h => <span key={h} style={{ fontSize: 11, fontWeight: 600, color: t.txD, textTransform: "uppercase", letterSpacing: .5 }}>{h}</span>)}
            </div>
          )}
          {invoices.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: t.txM, fontSize: 13 }}>No invoices yet</div>
          )}
          {invoices.map((inv, i) => bp.isMobile ? (
            <div key={i} style={{ padding: "12px 16px", borderBottom: i < invoices.length - 1 ? `1px solid ${t.bd2}` : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ color: t.p, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{inv.invoice_number}</span>
                <B color={t.g} bg={t.gS} t={t}>{inv.status}</B>
              </div>
              <div style={{ color: t.txM, fontSize: 12 }}>{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("en-IN") : "—"}</div>
              <div style={{ color: t.tx, fontSize: 14, fontWeight: 600, marginTop: 4 }}>₹{(inv.total || 0).toLocaleString("en-IN")}</div>
            </div>
          ) : (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "180px 1fr 100px 100px 80px", padding: "12px 20px", borderBottom: i < invoices.length - 1 ? `1px solid ${t.bd2}` : "none", gap: 12, alignItems: "center" }}>
              <span style={{ color: t.p, fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{inv.invoice_number}</span>
              <span style={{ color: t.tx2, fontSize: 13 }}>{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
              <span style={{ color: t.txM, fontSize: 13 }}>₹{(inv.subtotal || 0).toLocaleString("en-IN")}</span>
              <span style={{ color: t.txM, fontSize: 13 }}>₹{(inv.igst || inv.cgst + inv.sgst || 0).toLocaleString("en-IN")}</span>
              <span style={{ color: t.tx, fontSize: 13, fontWeight: 600 }}>₹{(inv.total || 0).toLocaleString("en-IN")}</span>
            </div>
          ))}
        </div>

        {/* Payment Method & GST Info */}
        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 40 }}>
          <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: t.tx, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><CreditCard size={14} color={t.txD} /> Payment Method</h4>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 40, height: 28, borderRadius: 4, background: t.sf2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: t.txM }}>VISA</div>
              <div>
                <div style={{ color: t.tx, fontSize: 13 }}>•••• •••• •••• 4242</div>
                <div style={{ color: t.txD, fontSize: 11 }}>Expires 12/28</div>
              </div>
              <div style={{ flex: 1 }} />
              <button style={{ background: "none", border: `1px solid ${t.bd}`, borderRadius: 6, padding: "5px 12px", color: t.txM, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Update</button>
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.bd2}`, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: t.txD }}>Supported:</span>
              {["Visa", "Mastercard", "RuPay", "UPI Autopay", "NACH"].map(m => (
                <B key={m} t={t}>{m}</B>
              ))}
            </div>
          </div>
          <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: 20 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: t.tx, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}><FileText size={14} color={t.txD} /> GST Details</h4>
            <div style={{ fontSize: 13, color: t.tx2, lineHeight: 1.8 }}>
              <div><span style={{ color: t.txM }}>SAC Code:</span> 997331</div>
              <div><span style={{ color: t.txM }}>GST Rate:</span> 18% (IGST or CGST 9% + SGST 9%)</div>
              <div><span style={{ color: t.txM }}>Tax type:</span> Auto-determined by buyer state</div>
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${t.bd2}` }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <B color={t.g} bg={t.gS} t={t}>DPDP Compliant</B>
                <B color={t.g} bg={t.gS} t={t}>E-Invoice Ready</B>
                <B t={t}>🇮🇳 India</B>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  TOAST NOTIFICATION SYSTEM                                     ║
// ╚═══════════════════════════════════════════════════════════════╝

let _toastId = 0;
let _toastListener = null;
const toast = {
  _fire(type, message, duration = 4000) {
    if (_toastListener) _toastListener({ id: ++_toastId, type, message, duration });
  },
  success(msg) { this._fire("success", msg); },
  error(msg) { this._fire("error", msg, 6000); },
  info(msg) { this._fire("info", msg); },
  warning(msg) { this._fire("warning", msg, 5000); },
};

const ToastContainer = ({ t }) => {
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    _toastListener = (toast) => {
      setToasts(prev => [...prev, toast]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toast.id)), toast.duration);
    };
    return () => { _toastListener = null; };
  }, []);
  if (!toasts.length) return null;
  const icons = { success: CheckCircle, error: AlertTriangle, info: Info, warning: AlertTriangle };
  const colors = { success: t.g, error: t.r, info: t.p, warning: t.o };
  const bgs = { success: t.gS, error: t.rS, info: t.pS, warning: t.oS };
  return (
    <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, maxWidth: 380 }}>
      {toasts.map(tt => { const Ic = icons[tt.type] || Info; return (
        <div key={tt.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", background: t.sf, border: `1px solid ${colors[tt.type]}33`, borderRadius: 10, boxShadow: t.shL, animation: "slideIn .25s ease" }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: bgs[tt.type], display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Ic size={14} color={colors[tt.type]} /></div>
          <span style={{ flex: 1, fontSize: 13, color: t.tx, lineHeight: 1.4 }}>{tt.message}</span>
          <button onClick={() => setToasts(prev => prev.filter(x => x.id !== tt.id))} style={{ background: "none", border: "none", cursor: "pointer", color: t.txD, padding: 2, display: "flex" }}><X size={14} /></button>
        </div>
      ); })}
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  ERROR BOUNDARY                                                ║
// ╚═══════════════════════════════════════════════════════════════╝

const ErrorFallback = ({ error, onReset, t }) => (
  <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
    <div style={{ textAlign: "center", maxWidth: 420 }}>
      <div style={{ width: 56, height: 56, borderRadius: 14, background: t.rS, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
        <AlertTriangle size={28} color={t.r} />
      </div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: t.tx, marginBottom: 8 }}>Something went wrong</h2>
      <p style={{ color: t.txM, fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
        {error?.message || "An unexpected error occurred. This has been logged for our team."}
      </p>
      <button onClick={onReset} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: t.p, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
        Try Again
      </button>
    </div>
  </div>
);

// Simple functional error boundary using key-based reset
const useErrorBoundary = () => {
  const [error, setError] = useState(null);
  const [resetKey, setResetKey] = useState(0);
  useEffect(() => {
    const handler = (e) => { setError(e.error || new Error("Unknown error")); e.preventDefault(); };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);
  const reset = () => { setError(null); setResetKey(k => k + 1); };
  return { error, reset, resetKey };
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  CONNECTOR SETUP PAGE                                          ║
// ╚═══════════════════════════════════════════════════════════════╝

const AVAILABLE_CONNECTORS = [
  { id: "google_workspace", name: "Google Workspace", icon: "🔵", desc: "Drive, Docs, Sheets, Gmail, Calendar", auth: "oauth", category: "Productivity" },
  { id: "slack", name: "Slack", icon: "💬", desc: "Channels, DMs, threads, files", auth: "oauth", category: "Communication" },
  { id: "microsoft_365", name: "Microsoft 365", icon: "🟦", desc: "OneDrive, SharePoint, Teams, Outlook", auth: "oauth", category: "Productivity" },
  { id: "jira", name: "Jira", icon: "🎯", desc: "Issues, sprints, boards, comments", auth: "oauth", category: "Engineering" },
  { id: "confluence", name: "Confluence", icon: "📘", desc: "Pages, spaces, blog posts", auth: "oauth", category: "Engineering" },
  { id: "notion", name: "Notion", icon: "📓", desc: "Pages, databases, wikis", auth: "oauth", category: "Productivity" },
  { id: "github", name: "GitHub", icon: "⚙️", desc: "Repos, issues, PRs, discussions", auth: "oauth", category: "Engineering" },
  { id: "zoho_crm", name: "Zoho CRM", icon: "🧩", desc: "Leads, deals, contacts, accounts", auth: "oauth", category: "Business" },
  { id: "zoho_desk", name: "Zoho Desk", icon: "🎧", desc: "Tickets, KB articles, contacts", auth: "oauth", category: "Business" },
  { id: "freshdesk", name: "Freshdesk", icon: "🎫", desc: "Tickets, knowledge base", auth: "apikey", category: "Business" },
  { id: "tally", name: "Tally ERP", icon: "📒", desc: "Ledgers, vouchers, stock items", auth: "apikey", category: "Finance" },
  { id: "darwinbox", name: "Darwinbox", icon: "👤", desc: "Employee directory, departments", auth: "apikey", category: "HR" },
  { id: "whatsapp", name: "WhatsApp Business", icon: "📱", desc: "Messages, templates, contacts", auth: "apikey", category: "Communication" },
  { id: "aws_s3", name: "AWS S3", icon: "☁️", desc: "Buckets, objects, documents", auth: "apikey", category: "Infrastructure" },
];

const ConnectorSetup = ({ go, bp, t, backendLive }) => {
  const [connected, setConnected] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [syncing, setSyncing] = useState({});
  const [filter, setFilter] = useState("all");
  const [apiKeyModal, setApiKeyModal] = useState(null);
  const [apiKey, setApiKey] = useState("");

  useEffect(() => {
    if (!backendLive) return;
    api.listConnectors().then(data => {
      setConnected((data.connectors || data || []).map(c => c.connector_type || c.id));
    }).catch(() => {});
  }, [backendLive]);

  const cats = ["all", ...new Set(AVAILABLE_CONNECTORS.map(c => c.category))];
  const filtered = filter === "all" ? AVAILABLE_CONNECTORS : AVAILABLE_CONNECTORS.filter(c => c.category === filter);

  const handleConnect = async (conn) => {
    if (conn.auth === "apikey") { setApiKeyModal(conn); return; }
    setConnecting(conn.id);
    try {
      if (backendLive) {
        const result = await api.listAvailableConnectors();
        window.open(`/api/v1/connectors/${conn.id}/connect?redirect=/connectors`, "_self");
      } else {
        await new Promise(r => setTimeout(r, 1500));
        setConnected(p => [...p, conn.id]);
        toast.success(`${conn.name} connected successfully!`);
      }
    } catch (e) {
      toast.error(`Failed to connect ${conn.name}`);
    }
    setConnecting(null);
  };

  const handleApiKeySubmit = async () => {
    if (!apiKey.trim() || !apiKeyModal) return;
    setConnecting(apiKeyModal.id);
    setApiKeyModal(null);
    try {
      if (backendLive) {
        await fetch(`/api/v1/connectors/${apiKeyModal.id}/connect/apikey`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ auth_config: { api_key: apiKey } }),
        });
      } else {
        await new Promise(r => setTimeout(r, 1500));
      }
      setConnected(p => [...p, apiKeyModal.id]);
      toast.success(`${apiKeyModal.name} connected!`);
    } catch { toast.error("Connection failed. Check your API key."); }
    setConnecting(null);
    setApiKey("");
  };

  const handleSync = async (connId) => {
    setSyncing(p => ({ ...p, [connId]: true }));
    try {
      if (backendLive) await api.triggerSync(connId);
      else await new Promise(r => setTimeout(r, 2000));
      toast.success("Sync started");
    } catch { toast.error("Sync failed"); }
    setSyncing(p => ({ ...p, [connId]: false }));
  };

  const isConnected = (id) => connected.includes(id);

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, margin: "0 0 4px" }}>Connect Your Tools</h2>
          <p style={{ color: t.txM, fontSize: 13, margin: 0 }}>COSFY searches across all your connected tools. Add your first connector to start indexing.</p>
        </div>

        {connected.length > 0 && (
          <div style={{ background: `linear-gradient(135deg, ${t.gS}, ${t.pS})`, borderRadius: 12, border: `1px solid ${t.g}22`, padding: 16, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
            <Wifi size={18} color={t.g} />
            <span style={{ flex: 1, color: t.tx, fontSize: 13, fontWeight: 500 }}>{connected.length} connector{connected.length > 1 ? "s" : ""} active</span>
            <B color={t.g} bg={t.gS} t={t}>Indexing</B>
          </div>
        )}

        <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
          {cats.map(c => (
            <button key={c} onClick={() => setFilter(c)} style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter === c ? t.p : t.bd}`, background: filter === c ? t.pS : "transparent", color: filter === c ? t.p : t.txM, fontSize: 12, fontWeight: 500, cursor: "pointer", textTransform: "capitalize", fontFamily: "inherit" }}>{c}</button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: bp.isMobile ? "1fr" : "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {filtered.map(conn => {
            const active = isConnected(conn.id);
            const loading = connecting === conn.id;
            return (
              <div key={conn.id} style={{ padding: 20, background: t.sf, borderRadius: 12, border: `1px solid ${active ? t.g + "44" : t.bd}`, display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 24 }}>{conn.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: t.tx, fontSize: 14, fontWeight: 600 }}>{conn.name}</div>
                    <div style={{ color: t.txD, fontSize: 11 }}>{conn.auth === "oauth" ? "OAuth 2.0" : "API Key"}</div>
                  </div>
                  {active && <B color={t.g} bg={t.gS} t={t}>Connected</B>}
                </div>
                <p style={{ color: t.txM, fontSize: 12, margin: "0 0 14px", lineHeight: 1.4, flex: 1 }}>{conn.desc}</p>
                {active ? (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleSync(conn.id)} disabled={syncing[conn.id]} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 0", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, cursor: "pointer", color: t.txM, fontSize: 12, fontWeight: 500, fontFamily: "inherit" }}>
                      <RefreshCw size={12} className={syncing[conn.id] ? "spin" : ""} /> {syncing[conn.id] ? "Syncing..." : "Sync Now"}
                    </button>
                    <button style={{ padding: "8px 12px", background: "none", border: `1px solid ${t.r}33`, borderRadius: 8, cursor: "pointer", color: t.r, fontSize: 12, fontFamily: "inherit" }}>Disconnect</button>
                  </div>
                ) : (
                  <button onClick={() => handleConnect(conn)} disabled={loading} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 0", background: t.p, border: "none", borderRadius: 8, cursor: loading ? "wait" : "pointer", color: "#fff", fontSize: 13, fontWeight: 600, fontFamily: "inherit", opacity: loading ? .7 : 1 }}>
                    {loading ? <><Loader size={14} /> Connecting...</> : <><LogIn size={14} /> Connect</>}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* API Key Modal */}
      {apiKeyModal && (
        <div onClick={() => setApiKeyModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: t.sf, borderRadius: 16, border: `1px solid ${t.bd}`, padding: 28, width: "100%", maxWidth: 420, boxShadow: t.shL }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <span style={{ fontSize: 28 }}>{apiKeyModal.icon}</span>
              <div><h3 style={{ fontSize: 16, fontWeight: 700, color: t.tx, margin: 0 }}>Connect {apiKeyModal.name}</h3><p style={{ color: t.txM, fontSize: 12, margin: "2px 0 0" }}>Enter your API key to connect</p></div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", color: t.tx2, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>API Key</label>
              <input value={apiKey} onChange={e => setApiKey(e.target.value)} type="password" placeholder="sk-..." autoFocus
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${t.bd}`, background: t.sf2, color: t.tx, fontSize: 14, outline: "none", fontFamily: "'DM Mono', monospace" }} />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn t={t} onClick={() => setApiKeyModal(null)} style={{ flex: 1 }}>Cancel</Btn>
              <Btn primary t={t} onClick={handleApiKeySubmit} style={{ flex: 1 }}>Connect</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  ONBOARDING WIZARD                                             ║
// ╚═══════════════════════════════════════════════════════════════╝

const OnboardingWizard = ({ show, onClose, go, t, bp }) => {
  const [step, setStep] = useState(0);
  if (!show) return null;

  const steps = [
    { title: "Welcome to COSFY", icon: "🚀", desc: "Your AI-powered Chief of Staff. COSFY searches across all your tools — Google Workspace, Slack, Jira, and 17 more — so your team finds answers in seconds, not hours." },
    { title: "Connect your first tool", icon: "🔗", desc: "COSFY needs access to your tools to search them. Start with Google Workspace or Slack — you can add more anytime. Setup takes 30 seconds.", action: "connectors" },
    { title: "Try your first search", icon: "🔍", desc: "Once connected, COSFY indexes your documents in minutes. Try searching for anything — a policy, a project name, a colleague's work. AI-powered answers cite the exact source." },
  ];

  const s = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ background: t.sf, borderRadius: 20, border: `1px solid ${t.bd}`, width: "100%", maxWidth: 480, overflow: "hidden", boxShadow: t.shL }}>
        {/* Progress */}
        <div style={{ display: "flex", gap: 4, padding: "16px 28px 0" }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? t.p : t.sf2, transition: "background .3s" }} />
          ))}
        </div>

        <div style={{ padding: "32px 28px", textAlign: "center" }}>
          <span style={{ fontSize: 48, display: "block", marginBottom: 16 }}>{s.icon}</span>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 10 }}>{s.title}</h2>
          <p style={{ color: t.txM, fontSize: 14, lineHeight: 1.7, maxWidth: 380, margin: "0 auto 28px" }}>{s.desc}</p>

          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{ padding: "10px 20px", borderRadius: 10, border: `1px solid ${t.bd}`, background: "transparent", color: t.txM, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Back</button>
            )}
            {s.action ? (
              <button onClick={() => { onClose(); go(s.action); }} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: t.p, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Connect Tools →
              </button>
            ) : isLast ? (
              <button onClick={onClose} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: t.p, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Start Searching →
              </button>
            ) : (
              <button onClick={() => setStep(step + 1)} style={{ padding: "10px 28px", borderRadius: 10, border: "none", background: t.p, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Next
              </button>
            )}
          </div>

          {!isLast && (
            <button onClick={onClose} style={{ background: "none", border: "none", color: t.txD, fontSize: 12, cursor: "pointer", marginTop: 16, fontFamily: "inherit" }}>Skip setup</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  TEAM INVITE PAGE                                              ║
// ╚═══════════════════════════════════════════════════════════════╝

const TeamInvite = ({ bp, t, backendLive }) => {
  const [emails, setEmails] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState([]);
  const [members] = useState([
    { name: "AG", email: "ag@aarama.in", role: "admin", status: "active", initials: "AG" },
    { name: "Priya Sharma", email: "priya@aarama.in", role: "member", status: "active", initials: "PS" },
    { name: "Arjun Mehta", email: "arjun@aarama.in", role: "member", status: "active", initials: "AM" },
  ]);

  const handleInvite = async () => {
    const list = emails.split(/[,\n]/).map(e => e.trim()).filter(e => e.includes("@"));
    if (!list.length) { toast.warning("Enter at least one valid email address"); return; }
    setSending(true);
    try {
      if (backendLive) {
        for (const email of list) {
          await fetch("/api/v1/auth/invite", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, role }),
          });
        }
      } else {
        await new Promise(r => setTimeout(r, 1500));
      }
      setSent(p => [...p, ...list]);
      setEmails("");
      toast.success(`${list.length} invitation${list.length > 1 ? "s" : ""} sent!`);
    } catch { toast.error("Failed to send invitations"); }
    setSending(false);
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: bp.isMobile ? 16 : 24 }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: t.tx, marginBottom: 4 }}>Team Members</h2>
        <p style={{ color: t.txM, fontSize: 13, marginBottom: 24 }}>Invite your team to search across all connected tools</p>

        {/* Invite Form */}
        <div style={{ background: t.sf, borderRadius: 14, border: `1px solid ${t.bd}`, padding: bp.isMobile ? 18 : 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}><UserPlus size={16} color={t.p} /> Invite Members</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", color: t.tx2, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Email addresses (comma or newline separated)</label>
            <textarea value={emails} onChange={e => setEmails(e.target.value)} rows={3} placeholder={"colleague@company.com\nteam@company.com"}
              style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${t.bd}`, background: t.sf2, color: t.tx, fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.6 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <label style={{ color: t.tx2, fontSize: 12, fontWeight: 600 }}>Role:</label>
            {["member", "admin"].map(r => (
              <button key={r} onClick={() => setRole(r)} style={{ padding: "5px 14px", borderRadius: 6, border: `1px solid ${role === r ? t.p : t.bd}`, background: role === r ? t.pS : "transparent", color: role === r ? t.p : t.txM, fontSize: 12, fontWeight: 500, cursor: "pointer", textTransform: "capitalize", fontFamily: "inherit" }}>{r}</button>
            ))}
          </div>
          <Btn primary t={t} onClick={handleInvite} style={{ opacity: sending ? .7 : 1 }}>
            {sending ? <><Loader size={14} /> Sending...</> : <><Mail size={14} /> Send Invitations</>}
          </Btn>
        </div>

        {/* Sent Invitations */}
        {sent.length > 0 && (
          <div style={{ background: t.gS, borderRadius: 10, border: `1px solid ${t.g}22`, padding: "12px 16px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: t.g, fontWeight: 600, marginBottom: 6 }}>Invitations Sent</div>
            {sent.map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 0" }}>
                <Check size={12} color={t.g} /><span style={{ fontSize: 12, color: t.tx2 }}>{e}</span>
              </div>
            ))}
          </div>
        )}

        {/* Current Members */}
        <h3 style={{ fontSize: 15, fontWeight: 600, color: t.tx, marginBottom: 12 }}>Current Members ({members.length})</h3>
        <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, overflow: "hidden" }}>
          {members.map((m, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: i < members.length - 1 ? `1px solid ${t.bd2}` : "none" }}>
              <Av ini={m.initials} sz={32} t={t} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: t.tx, fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                <div style={{ color: t.txD, fontSize: 12 }}>{m.email}</div>
              </div>
              <B t={t}>{m.role}</B>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: m.status === "active" ? t.g : t.txD }} />
            </div>
          ))}
        </div>

        {/* Invite Link */}
        <div style={{ background: t.sf, borderRadius: 12, border: `1px solid ${t.bd}`, padding: 16, marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: t.tx, marginBottom: 8 }}>Share invite link</div>
          <div style={{ display: "flex", gap: 8 }}>
            <input value="https://app.cosfy.in/join/aarama-abc123" readOnly style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.bd}`, background: t.sf2, color: t.txM, fontSize: 12, fontFamily: "'DM Mono', monospace", outline: "none" }} />
            <button onClick={() => { navigator.clipboard?.writeText("https://app.cosfy.in/join/aarama-abc123"); toast.success("Link copied!"); }}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "8px 14px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, cursor: "pointer", color: t.txM, fontSize: 12, fontWeight: 500, fontFamily: "inherit" }}>
              <Copy size={12} /> Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  LOGIN PAGE                                                    ║
// ╚═══════════════════════════════════════════════════════════════╝

// const LoginPage = ({ onLogin, onDemo, t }) => {
//   const [email, setEmail] = useState("");
//   const [pass, setPass] = useState("");
//   const [err, setErr] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [mode, setMode] = useState("login"); // login | register

//   const submit = async (e) => {
//     e.preventDefault();
//     if (!email.trim() || !pass.trim()) return;
//     setLoading(true); setErr(null);
//     try {
//       await onLogin(email, pass);
//     } catch (ex) {
//       setErr(ex.detail || ex.message || "Login failed. Check credentials or start the backend.");
//       setLoading(false);
//     }
//   };

//   return (
//     <div style={{ minHeight: "100vh", background: L.nv, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
//       <div style={{ width: "100%", maxWidth: 400 }}>
//         <div style={{ textAlign: "center", marginBottom: 32 }}>
//           <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
//             <Zap size={24} color="#fff" />
//           </div>
//           <h1 style={{ color: "#fff", fontSize: 28, fontWeight: 700, margin: "0 0 4px" }}>Cosfy</h1>
//           <p style={{ color: L.nvT, fontSize: 14 }}>Chief of Staff for You — Enterprise AI Search</p>
//         </div>

//         <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 16, border: "1px solid rgba(255,255,255,.1)", padding: 28 }}>
//           <form onSubmit={submit}>
//             <div style={{ marginBottom: 16 }}>
//               <label style={{ display: "block", color: L.nvT, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Email</label>
//               <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" autoFocus
//                 style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
//             </div>
//             <div style={{ marginBottom: 20 }}>
//               <label style={{ display: "block", color: L.nvT, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Password</label>
//               <input value={pass} onChange={e => setPass(e.target.value)} type="password" placeholder="••••••••"
//                 style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
//             </div>
//             {err && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(239,68,68,.15)", color: "#F87171", fontSize: 13, marginBottom: 16 }}>{err}</div>}
//             <button type="submit" disabled={loading}
//               style={{ width: "100%", padding: "11px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #6366F1, #818CF8)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? .7 : 1 }}>
//               {loading ? "Signing in..." : "Sign In"}
//             </button>
//           </form>

//           <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
//             <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
//             <span style={{ color: "rgba(255,255,255,.3)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>or</span>
//             <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
//           </div>

//           <button onClick={onDemo} style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
//             Continue in Demo Mode
//           </button>

//           <div style={{ display: "flex", gap: 8 }}>
//             <button style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
//               <Globe size={14} /> Google SSO
//             </button>
//             <button style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
//               <Shield size={14} /> Microsoft SSO
//             </button>
//           </div>
//         </div>

//         <p style={{ textAlign: "center", color: "rgba(255,255,255,.25)", fontSize: 11, marginTop: 20 }}>
//           Sovereign deployment · DPDP compliant · Indian data residency
//         </p>
//       </div>
//     </div>
//   );
// };



const LoginPage = ({ onLogin, onDemo, t }) => {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !pass.trim()) return;
    setLoading(true); setErr(null);
    try {
      await onLogin(email, pass);
    } catch (ex) {
      setErr(ex.detail || ex.message || "Login failed. Check credentials or start the backend.");
      setLoading(false);
    }
  };

  const loginAsAdmin = async () => {
    setLoading(true); setErr(null);
    try {
      await onLogin("admin@cosfy.com", "Admin@1234");
    } catch (ex) {
      setErr(ex.detail || ex.message || "Login failed");
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: L.nv, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 400 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <Zap size={24} color="#fff" />
          </div>
          <h1 style={{ color: "#fff", fontSize: 28, fontWeight: 700, margin: "0 0 4px" }}>Cosfy</h1>
          <p style={{ color: L.nvT, fontSize: 14 }}>Chief of Staff for You — Enterprise AI Search</p>
        </div>

        <div style={{ background: "rgba(255,255,255,.06)", borderRadius: 16, border: "1px solid rgba(255,255,255,.1)", padding: 28 }}>
          <form onSubmit={submit}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", color: L.nvT, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" autoFocus
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", color: L.nvT, fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Password</label>
              <input value={pass} onChange={e => setPass(e.target.value)} type="password" placeholder="••••••••"
                style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,.15)", background: "rgba(255,255,255,.06)", color: "#fff", fontSize: 14, outline: "none", fontFamily: "inherit" }} />
            </div>
            {err && (
              <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(239,68,68,.15)", color: "#F87171", fontSize: 13, marginBottom: 16 }}>
                {err}
              </div>
            )}
            <button type="submit" disabled={loading}
              style={{ width: "100%", padding: "11px 0", borderRadius: 10, border: "none", background: "linear-gradient(135deg, #6366F1, #818CF8)", color: "#fff", fontSize: 14, fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? .7 : 1 }}>
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
            <span style={{ color: "rgba(255,255,255,.3)", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>or</span>
            <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.1)" }} />
          </div>

          <button onClick={onDemo} style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>
            Continue in Demo Mode
          </button>

          {/* DEV ONLY — remove before production */}
          <button onClick={loginAsAdmin} disabled={loading}
            style={{ width: "100%", padding: "10px 0", borderRadius: 10, border: "1px solid rgba(99,102,241,.4)", background: "rgba(99,102,241,.1)", color: "#818CF8", fontSize: 13, fontWeight: 500, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", marginBottom: 10, opacity: loading ? .7 : 1 }}>
            🔧 Login as Backend Admin
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Globe size={14} /> Google SSO
            </button>
            <button style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "1px solid rgba(255,255,255,.15)", background: "transparent", color: L.nvT, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <Shield size={14} /> Microsoft SSO
            </button>
          </div>
        </div>

        <p style={{ textAlign: "center", color: "rgba(255,255,255,.25)", fontSize: 11, marginTop: 20 }}>
          Sovereign deployment · DPDP compliant · Indian data residency
        </p>
      </div>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  DEMO MODE BANNER                                             ║
// ╚═══════════════════════════════════════════════════════════════╝

const DemoBanner = ({ isLive, t }) => {
  const [dismissed, setDismissed] = useState(false);
  if (isLive || dismissed) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 16px", background: t.oS, borderBottom: `1px solid ${t.o}22`, fontSize: 12, color: t.o, fontWeight: 500 }}>
      <AlertTriangle size={13} />
      <span style={{ flex: 1 }}>Demo mode — showing mock data. Start the backend on port 8000 for live data.</span>
      <button onClick={() => setDismissed(true)} style={{ background: "none", border: "none", cursor: "pointer", color: t.o, padding: 2, display: "flex" }}><X size={14} /></button>
    </div>
  );
};

// ╔═══════════════════════════════════════════════════════════════╗
// ║  GLOBAL CSS                                                   ║
// ╚═══════════════════════════════════════════════════════════════╝

const Css = () => <style>{`
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;-webkit-font-smoothing:antialiased}
input::placeholder{color:inherit;opacity:.45}
::-webkit-scrollbar{width:6px;height:6px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:rgba(128,128,128,.2);border-radius:3px}
::-webkit-scrollbar-thumb:hover{background:rgba(128,128,128,.35)}
@keyframes pulse{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}
@keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
`}</style>;

// ╔═══════════════════════════════════════════════════════════════╗
// ║  APP                                                          ║
// ╚═══════════════════════════════════════════════════════════════╝

export default function App() {
  const auth = useAuth();
  const [page, setPage] = useState("home");
  const [query, setQuery] = useState("");
  const [mini, setMini] = useState(false);
  const [mob, setMob] = useState(false);
  const [mode, setMode] = useState("light");
  const [kbd, setKbd] = useState(false);
  const [backendLive, setBackendLive] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showClientWizard, setShowClientWizard] = useState(false);
  const searchRef = useRef(null);
  const t = mode === "dark" ? D : L;
  const bp = useBreakpoint();

  // Check if backend is reachable on mount
  useEffect(() => {
    api.healthCheck().then(() => setBackendLive(true)).catch(() => setBackendLive(false));
  }, []);

  // Show onboarding for first-time users
  useEffect(() => {
    if (auth.user && !localStorage.getItem("cosfy_onboarded")) {
      setShowOnboarding(true);
    }
  }, [auth.user]);

  const go = p => { setPage(p); setMob(false); };
  const doSearch = () => { if (query.trim()) go("search"); };

  // Keyboard shortcuts
  useEffect(() => {
    const h = e => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === "/" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setKbd(v => !v); }
      if (e.key === "Escape") { setKbd(false); setMob(false); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  useEffect(() => { if (bp.isMobile) setMini(false); }, [bp.isMobile]);

  // Show loading while checking auth
  if (auth.loading) {
    return (
      <div style={{ height: "100vh", background: L.nv, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Css />
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "linear-gradient(135deg, #6366F1, #D8FD49)", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
            <Zap size={24} color="#fff" />
          </div>
          <div style={{ color: L.nvT, fontSize: 14 }}>Loading Jaanch AI...</div>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!auth.user) {
    return (
      <>
        <Css />
        <LoginPage onLogin={auth.login} onDemo={auth.loginDemo} t={t} />
      </>
    );
  }

  // CA vertical → render the dedicated Jaanch AI shell (v15).
  // When workspace.vertical === "ca", the whole Cosfy shell is bypassed.
  if (auth.user?.workspace?.vertical === "ca" || auth.user?.org_vertical === "ca") {
    return (
      <>
        <Css />
        <JaanchAppV15 user={auth.user} onLogout={auth.logout} />
        <ToastContainer t={t} />
      </>
    );
  }

  // Lazy page rendering
  const renderPage = () => {
    switch (page) {
      case "home": return <HomePage go={go} setQ={setQuery} doSearch={doSearch} bp={bp} t={t} />;
      case "search": return <SearchPage q={query} bp={bp} t={t} user={auth.user} backendLive={backendLive} />;
      case "chat": return <ChatPage bp={bp} t={t} backendLive={backendLive} />;
      case "agents": return <AgentsPage bp={bp} t={t} />;
      case "people": return <PeoplePg bp={bp} t={t} />;
      case "collections": return <CollsPg bp={bp} t={t} />;
      case "golinks": return <GoLinksPg bp={bp} t={t} />;
      case "announces": return <AnnPg bp={bp} t={t} />;
      case "notifs": return <NotifPg t={t} bp={bp} />;
      case "activity": return <ActPg t={t} bp={bp} />;
      case "calendar": return <CalPg t={t} bp={bp} />;
      case "admin": return <AdminPg t={t} bp={bp} backendLive={backendLive} />;
      case "connectors": return <ConnectorSetup go={go} bp={bp} t={t} backendLive={backendLive} />;
      case "team": return <TeamInvite bp={bp} t={t} backendLive={backendLive} />;
      case "billing": return <BillingPage go={go} bp={bp} t={t} backendLive={backendLive} user={auth.user} />;
      case "pricing": return <PricingPage go={go} bp={bp} t={t} backendLive={backendLive} />;
      case "settings": return <SettingsPg mode={mode} setMode={setMode} t={t} bp={bp} user={auth.user} onLogout={auth.logout} />;
      case "gst_recon": return <GSTReconPage bp={bp} t={t} />;
      case "compliance": return <CompliancePage bp={bp} t={t} go={go} />;
      case "notices": return <NoticesPage bp={bp} t={t} />;
      case "ca_chat":    return <CAIntelligencePage bp={bp} t={t} backendLive={backendLive} go={go} />;
      case "documents": return <DocumentsPage bp={bp} t={t} />;
      default: return <HomePage go={go} setQ={setQuery} doSearch={doSearch} bp={bp} t={t} />;
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", background: t.bg, color: t.tx, fontFamily: "'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif", overflow: "hidden" }}>
      <Css />

      {/* Mobile overlay */}
      {mob && <div onClick={() => setMob(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 90, transition: "opacity .2s" }} />}

      {/* Sidebar */}
      {bp.isMobile ? (
        mob && <div style={{ position: "fixed", zIndex: 100, height: "100vh", left: 0, top: 0 }}>
          <SidebarInner page={page} go={go} mini={false} setMini={() => { }} t={t} />
        </div>
      ) : (
        <SidebarInner page={page} go={go} mini={mini} setMini={setMini} t={t} />
      )}

      {/* Main */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        <DemoBanner isLive={backendLive} t={t} />
        <TopBar q={query} setQ={setQuery} doSearch={doSearch} mode={mode} setMode={setMode} openMob={() => setMob(true)} isMobile={bp.isMobile} t={t} setKbd={setKbd} searchRef={searchRef} />
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {renderPage()}
        </div>
      </div>

      <KbdModal show={kbd} close={() => setKbd(false)} t={t} />
      <ToastContainer t={t} />
      <OnboardingWizard show={showOnboarding} onClose={() => { setShowOnboarding(false); localStorage.setItem("cosfy_onboarded", "1"); }} go={go} t={t} bp={bp} />
      <ClientOnboardingWizard
        show={showClientWizard}
        onClose={() => setShowClientWizard(false)}
        onComplete={data => { setShowClientWizard(false); go("compliance"); }}
        t={t} bp={bp}
      />
    </div>
  );
}
